import React, { useState, useEffect } from 'react'
import { motion } from 'framer-motion'
import { Routes, Route, NavLink, useNavigate, useLocation, useSearchParams } from 'react-router-dom'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { supabase } from '@/lib/supabase'
import { useAuth } from '@/lib/auth'
import { billingQueryKeys, TIER_NAMES, useWorkspaceAccess, useLimitCheck } from '@/lib/billing'
import type { PlanTier } from '@/lib/billing'
import { SHOW_BILLING_TAB, SHOW_APPEARANCE_TAB } from '@/lib/featureFlags'
import { useIsMobile } from '@/lib/useIsMobile'
import { getInitials, formatRelativeDate } from '@/lib/utils'
import {
  Building2, Users, CreditCard, Tag, Bell, Shield,
  ChevronRight, Check, Plus, Trash2, X, Upload,
  Zap, Globe, Clock, Palette, Save,
  Loader2, AlertCircle, CheckCircle2, Sparkles, Wrench, TrendingUp,
  Sun, Moon, BookOpen, Monitor,
  ArrowUp, ArrowDown,
  Receipt, History,
} from 'lucide-react'
import { useTheme, type Theme } from '@/lib/theme'
import { toast } from 'sonner'

const inp: React.CSSProperties = {
  width: '100%', padding: '9px 12px',
  background: 'var(--field-bg)',
  border: '1px solid var(--border-default)',
  borderRadius: 9, fontSize: 13, color: 'var(--ink-primary)',
  outline: 'none', boxSizing: 'border-box',
}

const SETTINGS_NAV = [
  { path: '', label: 'Workspace', icon: Building2 },
  { path: 'team', label: 'Team', icon: Users },
  { path: 'billing', label: 'Billing', icon: CreditCard },
  { path: 'invoicing', label: 'Invoicing', icon: Receipt },
  { path: 'ai', label: 'AI', icon: Sparkles },
  { path: 'service-lines', label: 'Service Lines', icon: Wrench },
  { path: 'pipeline', label: 'Pipeline', icon: TrendingUp },
  { path: 'audit', label: 'Audit log', icon: History },
  { path: 'terminology', label: 'Terminology', icon: Tag },
  { path: 'canned', label: 'Canned responses', icon: Zap },
  { path: 'notifications', label: 'Notifications', icon: Bell },
  { path: 'appearance', label: 'Appearance', icon: Palette },
  { path: 'security', label: 'Security', icon: Shield },
]

// ─── Section wrapper ──────────────────────────────────────────────────────────
function Section({ title, description, children }: any) {
  return (
    <div style={{ background: 'var(--surface)', border: '1px solid var(--border-subtle)', borderRadius: 16, overflow: 'hidden', marginBottom: 20 }}>
      <div style={{ padding: '16px 22px', borderBottom: '1px solid var(--border-subtle)' }}>
        <h3 style={{ fontSize: 14, fontWeight: 700, color: 'var(--ink-primary)', margin: 0 }}>{title}</h3>
        {description && <p style={{ fontSize: 12, color: 'var(--ink-tertiary)', margin: '3px 0 0' }}>{description}</p>}
      </div>
      <div style={{ padding: '20px 22px' }}>{children}</div>
    </div>
  )
}

// ─── Workspace Settings ───────────────────────────────────────────────────────
function WorkspaceSettings() {
  const { workspace, refreshWorkspace } = useAuth()
  const queryClient = useQueryClient()
  const [name, setName] = useState(workspace?.name || '')
  const [industry, setIndustry] = useState('')
  const [timezone, setTimezone] = useState('UTC')
  const [brandColor, setBrandColor] = useState(workspace?.brand_color || '#7c3aed')
  const [saving, setSaving] = useState(false)
  const [uploading, setUploading] = useState(false)

  useEffect(() => {
    if (workspace) {
      setName(workspace.name || '')
      setBrandColor(workspace.brand_color || '#7c3aed')
    }
  }, [workspace?.id])

  const handleSave = async () => {
    if (!workspace?.id) return
    setSaving(true)
    // Persist brand_color as NULL when matching the platform default so the
    // email renderer's `data.brandColor || '#7c3aed'` fallback engages cleanly.
    const brandColorToSave = brandColor.toLowerCase() === '#7c3aed' ? null : brandColor
    const { error } = await supabase.from('workspaces').update({
      name,
      brand_color: brandColorToSave,
      updated_at: new Date().toISOString(),
    }).eq('id', workspace.id)
    setSaving(false)
    if (error) { toast.error('Failed to save'); return }
    queryClient.invalidateQueries({ queryKey: ['workspace'] })
    await refreshWorkspace()
    toast.success('Workspace updated')
  }

  const handleLogoUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    // Reset the input value immediately so re-selecting the same file fires onChange again.
    e.target.value = ''
    if (!file || !workspace?.id) return

    if (file.size > 512000) {
      toast.error('Logo must be 500KB or smaller')
      return
    }
    const validTypes = ['image/png', 'image/jpeg', 'image/svg+xml']
    if (!validTypes.includes(file.type)) {
      toast.error('Logo must be PNG, JPEG, or SVG')
      return
    }

    setUploading(true)

    // Deterministic filename per workspace — overwrites on re-upload (no orphan
    // cleanup needed). Exception: re-uploading with a different extension
    // leaves the prior file as an orphan; acceptable for v1.
    const ext = file.name.split('.').pop()?.toLowerCase() || 'png'
    const path = `${workspace.id}/logo.${ext}`

    const { error: uploadError } = await supabase.storage
      .from('workspace-logos')
      .upload(path, file, { upsert: true, cacheControl: '3600' })

    if (uploadError) {
      setUploading(false)
      toast.error(`Upload failed: ${uploadError.message}`)
      return
    }

    const { data: { publicUrl } } = supabase.storage
      .from('workspace-logos')
      .getPublicUrl(path)

    // Cache-bust the public URL so the <img> preview reflects re-uploads
    // immediately. Supabase serves logos with cacheControl=3600; without
    // this query param, an upsert to the same path shows the previous
    // image until the browser cache expires.
    const cacheBustedUrl = `${publicUrl}?t=${Date.now()}`

    const { error: dbError } = await supabase.from('workspaces').update({
      logo_url: cacheBustedUrl,
      updated_at: new Date().toISOString(),
    }).eq('id', workspace.id)

    setUploading(false)

    if (dbError) {
      toast.error(`Logo uploaded but DB update failed: ${dbError.message}`)
      return
    }

    await refreshWorkspace()
    toast.success('Logo updated')
  }

  const handleLogoRemove = async () => {
    if (!workspace?.id || !workspace.logo_url) return
    setUploading(true)

    // Extract storage path from the public URL. Format:
    //   https://<project>.supabase.co/storage/v1/object/public/workspace-logos/<workspace_id>/logo.<ext>?t=...
    // Match everything between '/workspace-logos/' and the query string (or end).
    const match = workspace.logo_url.match(/\/workspace-logos\/(.+?)(\?|$)/)
    const path = match ? match[1] : null

    if (path) {
      // Best-effort delete; orphan files are acceptable per spec, so we
      // ignore storage errors and let the DB update proceed regardless.
      await supabase.storage.from('workspace-logos').remove([path])
    }

    const { error } = await supabase.from('workspaces').update({
      logo_url: null,
      updated_at: new Date().toISOString(),
    }).eq('id', workspace.id)

    setUploading(false)

    if (error) { toast.error(`Failed to remove logo: ${error.message}`); return }
    await refreshWorkspace()
    toast.success('Logo removed')
  }

  const INDUSTRIES = ['Home Services', 'Cleaning', 'HVAC', 'Plumbing', 'Electrical', 'Landscaping', 'Construction', 'IT Services', 'Consulting', 'Healthcare', 'Retail', 'Other']
  const TIMEZONES = ['UTC', 'America/New_York', 'America/Chicago', 'America/Denver', 'America/Los_Angeles', 'America/Phoenix', 'Europe/London', 'Europe/Paris', 'Asia/Tokyo', 'Australia/Sydney']

  return (
    <div>
      <Section title="General" description="Basic information about your workspace">
        <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
          <div>
            <label style={{ fontSize: 11, fontWeight: 600, color: 'var(--ink-tertiary)', display: 'block', marginBottom: 6, textTransform: 'uppercase', letterSpacing: '0.06em' }}>Workspace name</label>
            <input value={name} onChange={e => setName(e.target.value)} style={inp}
              onFocus={e => e.currentTarget.style.borderColor = 'var(--focus-ring)'}
              onBlur={e => e.currentTarget.style.borderColor = 'var(--border-default)'} />
          </div>
          <div>
            <label style={{ fontSize: 11, fontWeight: 600, color: 'var(--ink-tertiary)', display: 'block', marginBottom: 6, textTransform: 'uppercase', letterSpacing: '0.06em' }}>Industry</label>
            <select value={industry} onChange={e => setIndustry(e.target.value)} style={{ ...inp, cursor: 'pointer' }}>
              <option value="">Select industry...</option>
              {INDUSTRIES.map(i => <option key={i} value={i}>{i}</option>)}
            </select>
          </div>
          <div>
            <label style={{ fontSize: 11, fontWeight: 600, color: 'var(--ink-tertiary)', display: 'block', marginBottom: 6, textTransform: 'uppercase', letterSpacing: '0.06em' }}>Timezone</label>
            <select value={timezone} onChange={e => setTimezone(e.target.value)} style={{ ...inp, cursor: 'pointer' }}>
              {TIMEZONES.map(t => <option key={t} value={t}>{t}</option>)}
            </select>
          </div>
          <button onClick={handleSave} disabled={saving} className="btn-primary" style={{ width: 'fit-content' }}>
            <Save style={{ width: 14, height: 14 }} /> {saving ? 'Saving...' : 'Save changes'}
          </button>
        </div>
      </Section>

      <Section title="Branding" description="Shown on outbound quote and invoice emails to your customers">
        <div style={{ display: 'flex', flexDirection: 'column', gap: 22 }}>
          <div>
            <label style={{ fontSize: 11, fontWeight: 600, color: 'var(--ink-tertiary)', display: 'block', marginBottom: 10, textTransform: 'uppercase', letterSpacing: '0.06em' }}>Brand color</label>
            <div style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
              <input type="color" value={brandColor} onChange={e => setBrandColor(e.target.value)}
                style={{ width: 56, height: 56, border: '1px solid var(--border-default)', borderRadius: 12, cursor: 'pointer', background: 'transparent', padding: 4 }} />
              <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                  <code style={{ fontSize: 12, color: 'var(--ink-secondary)', fontFamily: 'monospace' }}>{brandColor.toUpperCase()}</code>
                  {brandColor.toLowerCase() !== '#7c3aed' && (
                    <button onClick={() => setBrandColor('#7c3aed')} className="btn-ghost" style={{ fontSize: 11, padding: '4px 10px' }}>
                      Reset to default
                    </button>
                  )}
                </div>
                <p style={{ fontSize: 11, color: 'var(--ink-tertiary)', margin: 0 }}>Used for email accents. Click "Save changes" above to apply.</p>
              </div>
            </div>
          </div>

          <div>
            <label style={{ fontSize: 11, fontWeight: 600, color: 'var(--ink-tertiary)', display: 'block', marginBottom: 10, textTransform: 'uppercase', letterSpacing: '0.06em' }}>Logo</label>
            <div style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
              <div style={{ width: 120, height: 56, borderRadius: 12, border: '1px solid var(--border-default)', background: 'var(--field-bg)', display: 'flex', alignItems: 'center', justifyContent: 'center', overflow: 'hidden' }}>
                {workspace?.logo_url ? (
                  <img src={workspace.logo_url} alt="Workspace logo" style={{ maxWidth: '100%', maxHeight: 40 }} />
                ) : (
                  <span style={{ fontSize: 11, color: 'var(--ink-tertiary)' }}>No logo</span>
                )}
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                <div style={{ display: 'flex', gap: 8 }}>
                  <label className="btn-ghost" style={{ fontSize: 12, padding: '7px 14px', cursor: uploading ? 'wait' : 'pointer', display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                    <Upload style={{ width: 13, height: 13 }} /> {uploading ? 'Uploading...' : (workspace?.logo_url ? 'Replace' : 'Upload logo')}
                    <input type="file" accept="image/png,image/jpeg,image/svg+xml" onChange={handleLogoUpload} disabled={uploading} style={{ display: 'none' }} />
                  </label>
                  {workspace?.logo_url && (
                    <button onClick={handleLogoRemove} disabled={uploading} className="btn-ghost" style={{ fontSize: 12, padding: '7px 14px', color: 'var(--danger)' }}>
                      Remove
                    </button>
                  )}
                </div>
                <p style={{ fontSize: 11, color: 'var(--ink-tertiary)', margin: 0 }}>PNG, JPEG, or SVG, max 500KB. Saves immediately on upload.</p>
              </div>
            </div>
          </div>
        </div>
      </Section>

      <Section title="Danger zone" description="Irreversible actions — proceed with caution">
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '14px 16px', background: 'var(--danger-dim)', border: '1px solid var(--danger-border)', borderRadius: 12 }}>
          <div>
            <p style={{ fontSize: 13, fontWeight: 600, color: 'var(--ink-primary)', margin: '0 0 3px' }}>Delete workspace</p>
            <p style={{ fontSize: 12, color: 'var(--ink-tertiary)', margin: 0 }}>Permanently delete this workspace and all data. Cannot be undone.</p>
          </div>
          <button style={{ padding: '8px 16px', background: 'var(--danger-dim)', border: '1px solid var(--danger-border)', borderRadius: 9, color: 'var(--danger)', fontSize: 12, fontWeight: 600, cursor: 'pointer', flexShrink: 0, marginLeft: 20 }}>
            Delete workspace
          </button>
        </div>
      </Section>
    </div>
  )
}

// ─── Team Settings ────────────────────────────────────────────────────────────
function TeamSettings() {
  const { workspace, profile } = useAuth()
  const queryClient = useQueryClient()
  const [inviteEmail, setInviteEmail] = useState('')
  const [inviteRole, setInviteRole] = useState<'admin' | 'agent' | 'viewer'>('agent')

  const { data: members = [] } = useQuery({
    queryKey: ['team', workspace?.id],
    queryFn: async () => {
      if (!workspace?.id) return []
      const { data } = await supabase.from('profiles').select('*').eq('workspace_id', workspace.id)
      return data || []
    },
    enabled: !!workspace?.id,
  })

  const { data: pendingInvites = [] } = useQuery({
    queryKey: ['workspace-invites', workspace?.id],
    queryFn: async () => {
      if (!workspace?.id) return []
      const { data } = await supabase
        .from('workspace_invites')
        .select('id, email, role, invited_by, created_at, expires_at, accepted_at')
        .eq('workspace_id', workspace.id)
        .is('accepted_at', null)
        .order('created_at', { ascending: false })
      return data || []
    },
    enabled: !!workspace?.id,
  })

  // Seat quota counts BOTH active members AND pending invites — issuing an
  // invite that would put the workspace over-cap on acceptance should be
  // blocked upfront, not silently allowed.
  const { atLimit, remaining, limit } = useLimitCheck('users', members.length + pendingInvites.length)
  const { data: workspaceAccess } = useWorkspaceAccess()
  const hasAccess = workspaceAccess?.hasAccess ?? true

  const canInvitePermissionWise =
    !!profile?.is_owner || profile?.role === 'admin'

  const inviteMutation = useMutation({
    mutationFn: async () => {
      const email = inviteEmail.trim().toLowerCase()
      if (!email) throw new Error('Enter an email address')
      const { data, error } = await supabase.functions.invoke('create-invite', {
        body: { email, role: inviteRole },
      })
      if (error) {
        // Pull the JSON body out of the gateway's wrapped error, same shape
        // as src/lib/email.ts handles.
        const ctx = (error as any).context
        let message = error.message
        if (ctx && typeof ctx.text === 'function') {
          try {
            const text = await ctx.text()
            const parsed = JSON.parse(text)
            message = parsed?.error || message
          } catch { /* keep default */ }
        }
        throw new Error(message)
      }
      return data
    },
    onSuccess: () => {
      toast.success(`Invite sent to ${inviteEmail}`)
      setInviteEmail('')
      queryClient.invalidateQueries({ queryKey: ['workspace-invites', workspace?.id] })
    },
    onError: (err: any) => {
      toast.error(err?.message || 'Failed to send invite')
    },
  })

  const revokeMutation = useMutation({
    mutationFn: async (inviteId: string) => {
      const { error } = await supabase.from('workspace_invites').delete().eq('id', inviteId)
      if (error) throw error
    },
    onSuccess: () => {
      toast.success('Invite revoked')
      queryClient.invalidateQueries({ queryKey: ['workspace-invites', workspace?.id] })
    },
    onError: (err: any) => toast.error(err?.message || 'Failed to revoke invite'),
  })

  const handleInvite = () => inviteMutation.mutate()
  const inviting = inviteMutation.isPending

  const blockedReason: string | null =
    !canInvitePermissionWise ? 'Only owners and admins can invite teammates.' :
    !hasAccess ? 'Your workspace billing is past due or canceled. Resolve it in Billing to invite teammates again.' :
    atLimit ? `Seat limit reached${limit ? ` (${limit} on ${TIER_NAMES[workspaceAccess?.tier as PlanTier] || 'your plan'})` : ''}. Upgrade your plan or revoke a pending invite to add another.` :
    null

  const ROLES = ['owner', 'admin', 'agent', 'viewer']
  // Each role pairs a foreground (token-colored) with a matching dim background.
  // Refactored from the prior single-string `'#hex'` shape so the template-literal
  // `${color}15` tint trick from the inline-hex era could be replaced with token
  // pairs that adapt across the three themes.
  const ROLE_COLORS: Record<string, { fg: string; bg: string }> = {
    owner:  { fg: 'var(--warning)',     bg: 'var(--warning-dim)' },
    admin:  { fg: 'var(--accent-text)', bg: 'var(--accent-dim)'  },
    agent:  { fg: 'var(--positive)',    bg: 'var(--positive-dim)' },
    viewer: { fg: 'var(--ink-tertiary)', bg: 'var(--surface)' },
  }

  return (
    <div>
      <Section title="Team members" description={`${members.length} member${members.length !== 1 ? 's' : ''} in your workspace`}>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          {members.map((member: any) => (
            <div key={member.id} style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '12px 14px', background: 'var(--surface)', borderRadius: 12, border: '1px solid var(--border-subtle)' }}>
              <div style={{ width: 36, height: 36, borderRadius: 10, background: 'linear-gradient(135deg, var(--accent), var(--positive))', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 13, fontWeight: 700, color: 'var(--ink-primary)', flexShrink: 0 }}>
                {getInitials(member.full_name || 'U')}
              </div>
              <div style={{ flex: 1 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <p style={{ fontSize: 13, fontWeight: 600, color: 'var(--ink-primary)', margin: 0 }}>{member.full_name || 'Unnamed'}</p>
                  {member.id === profile?.id && <span style={{ fontSize: 10, fontWeight: 700, color: 'var(--accent)', background: 'var(--accent-dim)', padding: '1px 6px', borderRadius: 100 }}>You</span>}
                </div>
              </div>
              <span style={{ fontSize: 11, fontWeight: 700, color: ROLE_COLORS[member.role]?.fg || 'var(--ink-primary)', background: ROLE_COLORS[member.role]?.bg || 'var(--border-subtle)', padding: '3px 10px', borderRadius: 100, textTransform: 'capitalize' }}>
                {member.role}
              </span>
            </div>
          ))}
        </div>
      </Section>

      <Section title="Invite team member" description={remaining == null ? 'Send an invitation to join your workspace' : `${remaining} seat${remaining === 1 ? '' : 's'} remaining`}>
        {blockedReason && (
          <div style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '12px 14px', background: 'var(--warning-dim)', border: '1px solid color-mix(in srgb, var(--warning) var(--status-tint-strong), transparent)', borderRadius: 10, marginBottom: 14 }}>
            <AlertCircle style={{ width: 16, height: 16, color: 'var(--warning)', flexShrink: 0 }} />
            <p style={{ fontSize: 12, color: 'var(--ink-secondary)', margin: 0 }}>{blockedReason}</p>
          </div>
        )}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr auto', gap: 10 }}>
            <div>
              <label style={{ fontSize: 11, fontWeight: 600, color: 'var(--ink-tertiary)', display: 'block', marginBottom: 6, textTransform: 'uppercase', letterSpacing: '0.06em' }}>Email address</label>
              <input type="email" value={inviteEmail} onChange={e => setInviteEmail(e.target.value)} placeholder="colleague@company.com" style={inp}
                onFocus={e => e.currentTarget.style.borderColor = 'var(--focus-ring)'}
                onBlur={e => e.currentTarget.style.borderColor = 'var(--border-default)'}
                onKeyDown={e => e.key === 'Enter' && !blockedReason && handleInvite()}
                disabled={!!blockedReason}
              />
            </div>
            <div>
              <label style={{ fontSize: 11, fontWeight: 600, color: 'var(--ink-tertiary)', display: 'block', marginBottom: 6, textTransform: 'uppercase', letterSpacing: '0.06em' }}>Role</label>
              <select value={inviteRole} onChange={e => setInviteRole(e.target.value as 'admin' | 'agent' | 'viewer')} disabled={!!blockedReason} style={{ ...inp, cursor: blockedReason ? 'not-allowed' : 'pointer', width: 120 }}>
                {ROLES.slice(1).map(r => <option key={r} value={r} style={{ textTransform: 'capitalize' }}>{r.charAt(0).toUpperCase() + r.slice(1)}</option>)}
              </select>
            </div>
          </div>
          <button onClick={handleInvite} disabled={inviting || !!blockedReason || !inviteEmail.trim()} className="btn-primary" style={{ width: 'fit-content' }}>
            <Plus style={{ width: 14, height: 14 }} /> {inviting ? 'Sending...' : 'Send invite'}
          </button>
        </div>
      </Section>

      {pendingInvites.length > 0 && (
        <Section title="Pending invites" description={`${pendingInvites.length} invite${pendingInvites.length === 1 ? '' : 's'} not yet accepted`}>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            {pendingInvites.map((invite: any) => {
              const expired = new Date(invite.expires_at) < new Date()
              return (
                <div key={invite.id} style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '12px 14px', background: 'var(--surface)', borderRadius: 12, border: '1px solid var(--border-subtle)' }}>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <p style={{ fontSize: 13, fontWeight: 600, color: 'var(--ink-primary)', margin: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{invite.email}</p>
                    <p style={{ fontSize: 11, color: 'var(--ink-tertiary)', margin: '3px 0 0' }}>
                      Sent {new Date(invite.created_at).toLocaleDateString()} · {expired ? 'Expired' : `Expires ${new Date(invite.expires_at).toLocaleDateString()}`}
                    </p>
                  </div>
                  <span style={{ fontSize: 11, fontWeight: 700, color: ROLE_COLORS[invite.role]?.fg || 'var(--ink-primary)', background: ROLE_COLORS[invite.role]?.bg || 'var(--border-subtle)', padding: '3px 10px', borderRadius: 100, textTransform: 'capitalize' }}>
                    {invite.role}
                  </span>
                  {expired && (
                    <span style={{ fontSize: 10, fontWeight: 700, color: 'var(--danger)', background: 'var(--danger-dim)', padding: '3px 8px', borderRadius: 100 }}>Expired</span>
                  )}
                  {canInvitePermissionWise && (
                    <button
                      onClick={() => { if (window.confirm(`Revoke invite to ${invite.email}?`)) revokeMutation.mutate(invite.id) }}
                      disabled={revokeMutation.isPending}
                      aria-label={`Revoke invite to ${invite.email}`}
                      title="Revoke invite"
                      style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--ink-tertiary)', padding: 6, display: 'flex', alignItems: 'center', borderRadius: 6 }}
                      onMouseEnter={e => { e.currentTarget.style.color = 'var(--danger)'; e.currentTarget.style.background = 'color-mix(in srgb, var(--danger) var(--status-tint-dim), transparent)' }}
                      onMouseLeave={e => { e.currentTarget.style.color = 'var(--ink-tertiary)'; e.currentTarget.style.background = 'transparent' }}
                    >
                      <Trash2 style={{ width: 14, height: 14 }} />
                    </button>
                  )}
                </div>
              )
            })}
          </div>
        </Section>
      )}

      <Section title="Roles & permissions" description="What each role can do">
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          {[
            { role: 'Owner',  fg: 'var(--warning)',      bg: 'var(--warning-dim)',  desc: 'Full access. Can delete workspace, manage billing, and all settings.' },
            { role: 'Admin',  fg: 'var(--accent-text)',  bg: 'var(--accent-dim)',   desc: 'Full access except billing and workspace deletion.' },
            { role: 'Agent',  fg: 'var(--positive)',     bg: 'var(--positive-dim)', desc: 'Can manage conversations, jobs, contacts, deals, and invoices.' },
            { role: 'Viewer', fg: 'var(--ink-tertiary)', bg: 'var(--surface)',      desc: 'Read-only access to all data. Cannot create or edit.' },
          ].map(r => (
            <div key={r.role} style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '10px 14px', background: 'var(--surface)', borderRadius: 10 }}>
              <span style={{ fontSize: 11, fontWeight: 700, color: r.fg, background: r.bg, padding: '3px 10px', borderRadius: 100, width: 60, textAlign: 'center', flexShrink: 0 }}>{r.role}</span>
              <p style={{ fontSize: 12, color: 'var(--ink-secondary)', margin: 0 }}>{r.desc}</p>
            </div>
          ))}
        </div>
      </Section>
    </div>
  )
}

// ─── Billing Settings ─────────────────────────────────────────────────────────
type BillingInterval = 'monthly' | 'yearly'

const PRICE_IDS: Record<string, Record<BillingInterval, string | undefined>> = {
  starter: {
    monthly: import.meta.env.VITE_STRIPE_PRICE_STARTER_MONTHLY,
    yearly:  import.meta.env.VITE_STRIPE_PRICE_STARTER_YEARLY,
  },
  growth: {
    monthly: import.meta.env.VITE_STRIPE_PRICE_GROWTH_MONTHLY,
    yearly:  import.meta.env.VITE_STRIPE_PRICE_GROWTH_YEARLY,
  },
  pro: {
    monthly: import.meta.env.VITE_STRIPE_PRICE_PRO_MONTHLY,
    yearly:  import.meta.env.VITE_STRIPE_PRICE_PRO_YEARLY,
  },
}

function BillingSettings() {
  const { workspace, profile, refreshWorkspace } = useAuth()
  const queryClient = useQueryClient()
  const [searchParams, setSearchParams] = useSearchParams()
  const [interval, setInterval] = useState<BillingInterval>('monthly')
  const [upgradingPlan, setUpgradingPlan] = useState<string | null>(null)
  const [activating, setActivating] = useState(false)
  const [openingPortal, setOpeningPortal] = useState(false)

  const success  = searchParams.get('success')  === 'true'
  const canceled = searchParams.get('canceled') === 'true'

  // After Stripe redirects back with ?success=true, poll the subscriptions
  // table until the webhook lands and the row appears. The webhook is fast
  // (~138ms in testing) but the redirect can occasionally beat it.
  useEffect(() => {
    if (!success || !workspace?.id) return

    setActivating(true)
    let attempts = 0
    const maxAttempts = 20  // 20 × 500ms = 10s
    let cancelled = false

    const poll = async () => {
      while (!cancelled && attempts < maxAttempts) {
        attempts++
        const { data } = await supabase
          .from('subscriptions')
          .select('id, status')
          .eq('workspace_id', workspace.id)
          .in('status', ['active', 'trialing'])
          .maybeSingle()

        if (data) {
          await refreshWorkspace()
          // Invalidate the billing query caches so gates immediately reflect the
          // new plan/access state instead of serving stale "no access" answers
          // until staleTime expires (5min) or the user refreshes.
          await queryClient.invalidateQueries({ queryKey: billingQueryKeys.access(workspace.id) })
          await queryClient.invalidateQueries({ queryKey: billingQueryKeys.limits(workspace.id) })
          if (!cancelled) {
            setActivating(false)
            toast.success('Subscription activated')
            // Clear query params so a refresh doesn't re-trigger the banner.
            setSearchParams({}, { replace: true })
          }
          return
        }
        await new Promise(r => setTimeout(r, 500))
      }
      if (!cancelled) {
        setActivating(false)
        // Don't clear params — leave the banner up so the user knows to refresh.
      }
    }
    poll()

    return () => { cancelled = true }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [success, workspace?.id])

  // Auto-clear the canceled banner after 6 seconds.
  useEffect(() => {
    if (!canceled) return
    const t = setTimeout(() => setSearchParams({}, { replace: true }), 6000)
    return () => clearTimeout(t)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [canceled])

  const handleUpgrade = async (planId: string) => {
    if (!profile?.is_owner) {
      toast.error('Only the workspace owner can manage billing')
      return
    }

    const priceId = PRICE_IDS[planId]?.[interval]
    if (!priceId) {
      toast.error('Price not configured. Check VITE_STRIPE_PRICE_* env vars.')
      return
    }

    setUpgradingPlan(planId)
    try {
      const returnUrl = `${window.location.origin}/app/settings/billing`
      const { data, error } = await supabase.functions.invoke<{ url: string }>(
        'create-checkout-session',
        {
          body: {
            price_id:    priceId,
            success_url: `${returnUrl}?success=true`,
            cancel_url:  `${returnUrl}?canceled=true`,
          },
        },
      )

      if (error) {
        // supabase.functions.invoke doesn't surface response bodies on non-2xx
        // by default — read the body for a useful message. Same pattern as
        // lib/email.ts / lib/ai.ts: build a message string, never throw inside
        // the try (a bare catch can't distinguish a parse failure from our own
        // deliberate throw).
        //
        // Two shapes possible:
        //   - Our function:    { error: "..." }
        //   - Supabase gateway: { code: "...", message: "..." }
        const ctx = (error as any).context
        let message = error.message
        if (ctx && typeof ctx.text === 'function') {
          try {
            const text = await ctx.text()
            const parsed = JSON.parse(text)
            message = parsed?.error || parsed?.message || message
          } catch { /* keep default */ }
        }
        throw new Error(message)
      }
      if (!data?.url) throw new Error('No checkout URL returned')

      window.location.href = data.url
    } catch (err) {
      console.error('Checkout error:', err)
      toast.error(err instanceof Error ? err.message : 'Failed to start checkout')
      setUpgradingPlan(null)
    }
  }

  const handleOpenPortal = async () => {
    setOpeningPortal(true)
    try {
      const { data, error } = await supabase.functions.invoke<{ url: string }>(
        'create-portal-session',
        {
          body: {
            return_url: `${window.location.origin}/app/settings/billing`,
          },
        },
      )

      if (error) {
        // Same body-extraction pattern as handleUpgrade — see comment there
        // for shape details.
        const ctx = (error as any).context
        let message = error.message
        if (ctx && typeof ctx.text === 'function') {
          try {
            const text = await ctx.text()
            const parsed = JSON.parse(text)
            message = parsed?.error || parsed?.message || message
          } catch { /* keep default */ }
        }
        throw new Error(message)
      }
      if (!data?.url) throw new Error('No portal URL returned')

      window.location.href = data.url
    } catch (err) {
      console.error('Portal error:', err)
      toast.error(err instanceof Error ? err.message : 'Failed to open billing portal')
      setOpeningPortal(false)
    }
  }

  const PLANS = [
    { id: 'starter', name: 'Starter', monthly: 49,  yearly: 470,  features: ['Up to 3 users', 'Contacts & Inbox', 'Deals & Invoices', '1,000 contacts', 'Email support'] },
    { id: 'growth',  name: 'Growth',  monthly: 99,  yearly: 950,  features: ['Up to 10 users', 'All Starter features', 'Field Hub & Projects', 'Reputation Hub', '5,000 contacts', 'Priority support'], popular: true },
    { id: 'pro',     name: 'Pro',     monthly: 199, yearly: 1910, features: ['Up to 40 users', 'All Growth features', 'Custom integrations', 'Advanced analytics', '10,000 contacts', 'Dedicated support'] },
  ]

  const currentPlan = workspace?.plan_tier || 'starter'
  const isOwner = !!profile?.is_owner

  return (
    <div>
      {/* Banners ─────────────────────────────────────────────────────────── */}
      {success && activating && (
        <div style={bannerStyle('var(--accent-dim)', 'var(--accent-border)')}>
          <Loader2 style={{ width: 16, height: 16, color: 'var(--accent-text)', animation: 'spin 1s linear infinite' }} />
          <span style={{ fontSize: 13, color: 'var(--ink-primary)' }}>Activating your subscription…</span>
        </div>
      )}
      {success && !activating && (
        <div style={bannerStyle('var(--danger-dim)', 'var(--danger-border)')}>
          <AlertCircle style={{ width: 16, height: 16, color: 'var(--danger)' }} />
          <span style={{ fontSize: 13, color: 'var(--ink-primary)' }}>
            Payment received but we're still confirming. Refresh in a moment.
          </span>
        </div>
      )}
      {canceled && (
        <div style={bannerStyle('var(--hover-bg)', 'var(--border-default)')}>
          <AlertCircle style={{ width: 16, height: 16, color: 'var(--ink-secondary)' }} />
          <span style={{ fontSize: 13, color: 'var(--ink-primary)' }}>Checkout canceled. No changes were made.</span>
        </div>
      )}

      <Section title="Current plan" description={`Your workspace is on the ${TIER_NAMES[currentPlan as PlanTier] || 'Starter'} plan`}>
        {/* Monthly / Yearly toggle ──────────────────────────────────────── */}
        <div style={{ display: 'flex', justifyContent: 'center', marginBottom: 18 }}>
          <div style={{ display: 'inline-flex', background: 'var(--hover-bg)', border: '1px solid var(--border-subtle)', borderRadius: 10, padding: 3 }}>
            {(['monthly', 'yearly'] as BillingInterval[]).map(opt => (
              <button
                key={opt}
                onClick={() => setInterval(opt)}
                style={{
                  padding: '6px 16px', borderRadius: 8, fontSize: 12, fontWeight: 600,
                  background: interval === opt ? 'var(--accent-dim)' : 'transparent',
                  color: interval === opt ? 'var(--accent-text)' : 'var(--ink-secondary)',
                  border: 'none', cursor: 'pointer', transition: 'all 150ms ease',
                }}
              >
                {opt === 'monthly' ? 'Monthly' : 'Yearly'}
                {opt === 'yearly' && (
                  <span style={{ marginLeft: 6, fontSize: 10, color: 'var(--positive)', fontWeight: 700 }}>−20%</span>
                )}
              </button>
            ))}
          </div>
        </div>

        {/* Plan cards — auto-fit (280px min) so the 3-up desktop layout
            wraps to 2-up at narrow desktop widths and 1-up on mobile
            instead of compressing into unreadable strips. */}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: 14 }}>
          {PLANS.map(plan => {
            const isCurrent = currentPlan === plan.id
            const price     = interval === 'monthly' ? plan.monthly : plan.yearly
            const suffix    = interval === 'monthly' ? '/mo' : '/yr'
            const isLoading = upgradingPlan === plan.id

            // Disable Upgrade for non-owners and during any in-flight upgrade.
            const disabled = isCurrent || !isOwner || upgradingPlan !== null

            let buttonLabel: React.ReactNode = 'Upgrade'
            if (isCurrent)        buttonLabel = 'Current plan'
            else if (!isOwner)    buttonLabel = 'Owner only'
            else if (isLoading)   buttonLabel = <Loader2 style={{ width: 13, height: 13, animation: 'spin 1s linear infinite' }} />

            return (
              <div key={plan.id} style={{ background: isCurrent ? 'var(--accent-dim)' : 'var(--surface)', border: `1px solid ${isCurrent ? 'var(--accent-border)' : 'var(--border-subtle)'}`, borderRadius: 16, padding: '20px', position: 'relative', overflow: 'hidden' }}>
                {plan.popular && (
                  <div style={{ position: 'absolute', top: 12, right: 12, fontSize: 10, fontWeight: 700, color: 'var(--accent)', background: 'var(--accent-dim)', padding: '2px 8px', borderRadius: 100 }}>Popular</div>
                )}
                <h3 style={{ fontSize: 16, fontWeight: 800, color: 'var(--ink-primary)', margin: '0 0 4px' }}>{plan.name}</h3>
                <div style={{ display: 'flex', alignItems: 'baseline', gap: 4, marginBottom: 16 }}>
                  <span style={{ fontSize: 28, fontWeight: 900, color: 'var(--ink-primary)' }}>${price}</span>
                  <span style={{ fontSize: 12, color: 'var(--ink-tertiary)' }}>{suffix}</span>
                </div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 7, marginBottom: 16 }}>
                  {plan.features.map(f => (
                    <div key={f} style={{ display: 'flex', alignItems: 'center', gap: 7 }}>
                      <Check style={{ width: 12, height: 12, color: isCurrent ? 'var(--accent-text)' : 'var(--positive)', flexShrink: 0 }} />
                      <span style={{ fontSize: 12, color: 'var(--ink-secondary)' }}>{f}</span>
                    </div>
                  ))}
                </div>
                <button
                  disabled={disabled}
                  onClick={() => handleUpgrade(plan.id)}
                  style={{
                    width: '100%', padding: '9px', borderRadius: 10, fontSize: 12, fontWeight: 700,
                    cursor: disabled ? 'default' : 'pointer',
                    background: isCurrent ? 'var(--accent-dim)' : (disabled ? 'var(--hover-bg)' : 'var(--accent-dim)'),
                    border: isCurrent ? '1px solid var(--accent-border)' : '1px solid var(--accent-border)',
                    color: isCurrent ? 'var(--accent-text)' : (disabled ? 'var(--ink-tertiary)' : 'var(--accent-text)'),
                    display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6,
                    transition: 'all 150ms ease',
                  }}
                >
                  {buttonLabel}
                </button>
              </div>
            )
          })}
        </div>

        {!isOwner && (
          <p style={{ fontSize: 11, color: 'var(--ink-tertiary)', marginTop: 12, textAlign: 'center' }}>
            Only the workspace owner can change the plan.
          </p>
        )}
      </Section>

      <Section title="Payment method" description="Manage your billing details">
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '14px 16px', background: 'var(--surface)', border: '1px solid var(--border-subtle)', borderRadius: 12 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            <div style={{ width: 40, height: 28, background: 'linear-gradient(135deg, var(--surface-raised), var(--surface-sunken))', borderRadius: 6, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 14 }}>💳</div>
            <div>
              <p style={{ fontSize: 13, fontWeight: 600, color: 'var(--ink-primary)', margin: 0 }}>
                {currentPlan === 'starter' ? 'No payment method' : 'Card on file'}
              </p>
              <p style={{ fontSize: 11, color: 'var(--ink-tertiary)', margin: 0 }}>
                {currentPlan === 'starter' ? 'Add a card to upgrade your plan' : 'Managed via Stripe'}
              </p>
            </div>
          </div>
          {isOwner && currentPlan !== 'starter' && (
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 4 }}>
              <button
                onClick={handleOpenPortal}
                disabled={openingPortal}
                style={{
                  padding: '7px 14px', borderRadius: 10, fontSize: 12, fontWeight: 700,
                  background: openingPortal ? 'var(--hover-bg)' : 'var(--accent-dim)',
                  border: '1px solid var(--accent-border)',
                  color: openingPortal ? 'var(--ink-tertiary)' : 'var(--accent-text)',
                  cursor: openingPortal ? 'default' : 'pointer',
                  display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6,
                  transition: 'all 150ms ease',
                }}
              >
                {openingPortal
                  ? <Loader2 style={{ width: 13, height: 13, animation: 'spin 1s linear infinite' }} />
                  : 'Manage subscription'}
              </button>
              <p style={{ fontSize: 10, color: 'var(--ink-tertiary)', margin: 0 }}>
                Update payment method, change plan, or cancel
              </p>
            </div>
          )}
        </div>
      </Section>
    </div>
  )
}

function bannerStyle(bg: string, border: string): React.CSSProperties {
  return {
    display: 'flex', alignItems: 'center', gap: 10,
    background: bg, border: `1px solid ${border}`,
    borderRadius: 12, padding: '12px 16px', marginBottom: 16,
  }
}

// ─── AI Settings ──────────────────────────────────────────────────────────────
// Workspace-level AI opt-out. Persists to workspaces.ai_enabled; the SQL
// function get_workspace_limits filters 'ai' from the features array when
// FALSE, so the Edge Function gate, useFeature('ai'), and GatedRoute all
// pick up the change automatically. See migration 20260502120000.
//
// Visibility note: this section is only reachable when the workspace's
// tier supports AI (pro/enterprise) — see the SETTINGS_NAV filter at the
// bottom of this file. Filtering on tier (not on the current opt-in
// state) prevents the toggle-off-hides-the-way-back-on bug.
function AISettings() {
  const { workspace, refreshWorkspace } = useAuth()
  const queryClient = useQueryClient()
  const [saving, setSaving] = useState(false)

  if (!workspace) return null
  const enabled = workspace.ai_enabled ?? true

  const handleToggle = async () => {
    if (saving) return
    setSaving(true)
    const next = !enabled
    const { error } = await supabase
      .from('workspaces')
      .update({ ai_enabled: next })
      .eq('id', workspace.id)
    if (error) {
      setSaving(false)
      toast.error(`Failed to ${next ? 'enable' : 'disable'} AI: ${error.message}`)
      return
    }
    await refreshWorkspace()
    await queryClient.invalidateQueries({ queryKey: billingQueryKeys.limits(workspace.id) })
    setSaving(false)
    toast.success(next ? 'AI enabled for this workspace' : 'AI disabled for this workspace')
  }

  return (
    <div>
      <Section title="AI features" description="Control whether your workspace uses AI features (powered by Anthropic Claude)">
        <div onClick={handleToggle}
          style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 16, padding: '14px 16px', background: 'var(--surface)', border: '1px solid var(--border-subtle)', borderRadius: 10, cursor: saving ? 'wait' : 'pointer', transition: 'background 150ms ease', opacity: saving ? 0.6 : 1 }}
          onMouseEnter={e => { if (!saving) e.currentTarget.style.background = 'var(--hover-bg)' }}
          onMouseLeave={e => { if (!saving) e.currentTarget.style.background = 'var(--surface)' }}>
          <div style={{ flex: 1, minWidth: 0 }}>
            <p style={{ fontSize: 13, color: 'var(--ink-primary)', margin: '0 0 4px', fontWeight: 600 }}>AI features enabled</p>
            <p style={{ fontSize: 12, color: 'var(--ink-secondary)', margin: 0, lineHeight: 1.5 }}>
              When off, AI Hub and the floating AI assistant are hidden, and AI requests are blocked server-side. Your workspace data is not sent to Anthropic while this is off.
            </p>
          </div>
          <div style={{ width: 36, height: 20, borderRadius: 100, background: enabled ? 'var(--accent)' : 'var(--border-default)', transition: 'background 200ms ease', position: 'relative', flexShrink: 0, marginTop: 2 }}>
            <div style={{ position: 'absolute', top: 3, left: enabled ? 19 : 3, width: 14, height: 14, borderRadius: '50%', background: 'var(--accent-ink)', transition: 'left 200ms ease', boxShadow: 'var(--shadow-knob)' }} />
          </div>
        </div>
      </Section>
    </div>
  )
}

// ─── Invoicing Settings ───────────────────────────────────────────────────────
//
// Customer-facing reminder cadence config — per-WORKSPACE, stored in
// workspaces.settings.invoice_reminders. Distinct from the per-USER
// invoice_overdue toggle in NotificationsSettings (which lives in
// profiles.notification_preferences and is a "notify me when..." switch
// for the operator, not a "send my customer a reminder" config).
//
// Shape on the wire:
//   { enabled: boolean, days_before_due: number[], days_after_due: number[] }
//
// Default (pre-populated for a workspace with no config yet; only persists
// when the operator clicks Save):
//   { enabled: true, days_before_due: [3], days_after_due: [0, 7, 14] }
//
// Piece 4 (the scheduler) reads workspaces.settings.invoice_reminders via
// service-role; the actual cancellation when an invoice is paid/voided
// also belongs to Piece 4. This component is read/write of the config only.

type InvoiceRemindersConfig = {
  enabled: boolean
  days_before_due: number[]
  days_after_due: number[]
}

const DEFAULT_INVOICE_REMINDERS: InvoiceRemindersConfig = {
  enabled: true,
  days_before_due: [3],
  days_after_due: [0, 7, 14],
}

function InvoicingSettings() {
  const { workspace, refreshWorkspace } = useAuth()
  const queryClient = useQueryClient()

  // Read the persisted config or fall back to the sensible default. Never
  // auto-persist the default — only the operator's explicit Save writes
  // the JSONB. A workspace with no invoice_reminders key sees the form
  // pre-filled but `dirty` reports true so the Save button stays primary
  // (operator can just hit Save to lock in the defaults, or tweak first).
  const persistedSettings = (workspace?.settings as Record<string, unknown> | null | undefined) ?? {}
  const persistedConfig = (persistedSettings.invoice_reminders ?? null) as InvoiceRemindersConfig | null

  const [config, setConfig] = useState<InvoiceRemindersConfig>(persistedConfig ?? DEFAULT_INVOICE_REMINDERS)
  const [newBefore, setNewBefore] = useState('')
  const [newAfter, setNewAfter] = useState('')
  const [saving, setSaving] = useState(false)

  // Re-sync the form when the workspace switches or refreshWorkspace
  // updates the underlying value. JSON.stringify compares deeply at low
  // cost (config objects are tiny — a handful of numbers).
  useEffect(() => {
    setConfig(persistedConfig ?? DEFAULT_INVOICE_REMINDERS)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [workspace?.id, JSON.stringify(persistedConfig)])

  if (!workspace) return null

  // Dirty against the last-persisted config (or DEFAULT for first-time;
  // dirty=true so the Save button looks primary on first visit).
  const persisted = persistedConfig ?? DEFAULT_INVOICE_REMINDERS
  const dirty = persistedConfig === null || JSON.stringify(config) !== JSON.stringify(persisted)

  const toggleEnabled = () => setConfig(c => ({ ...c, enabled: !c.enabled }))

  const addBefore = () => {
    const n = parseInt(newBefore, 10)
    if (!Number.isFinite(n) || n < 1) { toast.error('Enter a number of days (1 or more)'); return }
    if (config.days_before_due.includes(n)) { toast.error('That reminder is already in the list'); return }
    setConfig(c => ({ ...c, days_before_due: [...c.days_before_due, n].sort((a, b) => b - a) }))
    setNewBefore('')
  }
  const removeBefore = (n: number) =>
    setConfig(c => ({ ...c, days_before_due: c.days_before_due.filter(d => d !== n) }))

  const addAfter = () => {
    const n = parseInt(newAfter, 10)
    if (!Number.isFinite(n) || n < 0) { toast.error('Enter zero or a positive number of days'); return }
    if (config.days_after_due.includes(n)) { toast.error('That reminder is already in the list'); return }
    setConfig(c => ({ ...c, days_after_due: [...c.days_after_due, n].sort((a, b) => a - b) }))
    setNewAfter('')
  }
  const removeAfter = (n: number) =>
    setConfig(c => ({ ...c, days_after_due: c.days_after_due.filter(d => d !== n) }))

  const handleSave = async () => {
    if (!workspace?.id || saving) return
    setSaving(true)
    // Spread-merge so future workspaces.settings keys (AI prefs, portal
    // defaults, etc.) survive untouched when only invoice_reminders changes.
    const nextSettings = { ...persistedSettings, invoice_reminders: config }
    const { error } = await supabase
      .from('workspaces')
      .update({ settings: nextSettings, updated_at: new Date().toISOString() })
      .eq('id', workspace.id)
    setSaving(false)
    if (error) { toast.error(`Failed to save: ${error.message}`); return }
    queryClient.invalidateQueries({ queryKey: ['workspace'] })
    await refreshWorkspace()
    toast.success('Reminder settings saved')
  }

  const groupLabel: React.CSSProperties = {
    fontSize: 11, fontWeight: 700, color: 'var(--ink-tertiary)',
    textTransform: 'uppercase', letterSpacing: '0.08em', margin: '0 0 10px',
  }
  const offsetRow: React.CSSProperties = {
    display: 'flex', alignItems: 'center', justifyContent: 'space-between',
    padding: '11px 14px', background: 'var(--surface)',
    border: '1px solid var(--border-subtle)', borderRadius: 10,
  }
  const emptyHint: React.CSSProperties = {
    padding: '12px 14px', background: 'var(--surface)',
    border: '1px dashed var(--border-default)', borderRadius: 10,
    fontSize: 12, color: 'var(--ink-tertiary)', textAlign: 'center',
  }

  return (
    <div>
      <Section title="Customer reminders" description="Automatically email customers reminders about their invoices on a schedule you set.">
        {/* Master toggle */}
        <div onClick={toggleEnabled}
          style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 16, padding: '14px 16px', background: 'var(--surface)', border: '1px solid var(--border-subtle)', borderRadius: 10, cursor: 'pointer', marginBottom: 18, transition: 'background 150ms ease' }}
          onMouseEnter={e => e.currentTarget.style.background = 'var(--hover-bg)'}
          onMouseLeave={e => e.currentTarget.style.background = 'var(--surface)'}>
          <div style={{ flex: 1, minWidth: 0 }}>
            <p style={{ fontSize: 13, color: 'var(--ink-primary)', margin: '0 0 4px', fontWeight: 600 }}>Send automatic reminders</p>
            <p style={{ fontSize: 12, color: 'var(--ink-secondary)', margin: 0, lineHeight: 1.5 }}>
              When off, no reminder emails are sent regardless of the cadence below. Reminders also stop automatically once an invoice is paid or voided.
            </p>
          </div>
          <div style={{ width: 36, height: 20, borderRadius: 100, background: config.enabled ? 'var(--accent)' : 'var(--border-default)', transition: 'background 200ms ease', position: 'relative', flexShrink: 0, marginTop: 2 }}>
            <div style={{ position: 'absolute', top: 3, left: config.enabled ? 19 : 3, width: 14, height: 14, borderRadius: '50%', background: 'var(--accent-ink)', transition: 'left 200ms ease', boxShadow: 'var(--shadow-knob)' }} />
          </div>
        </div>

        {/* Cadence editor — dims + ignores pointer events when master toggle is off */}
        <div style={{ opacity: config.enabled ? 1 : 0.55, pointerEvents: config.enabled ? 'auto' : 'none', transition: 'opacity 200ms ease' }}>
          {/* Before the due date */}
          <div style={{ marginBottom: 22 }}>
            <p style={groupLabel}>Before the due date</p>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 6, marginBottom: 10 }}>
              {config.days_before_due.length === 0 ? (
                <div style={emptyHint}>No reminders scheduled — add one below.</div>
              ) : config.days_before_due.map(n => (
                <div key={n} style={offsetRow}>
                  <span style={{ fontSize: 13, color: 'var(--ink-secondary)' }}>{n} {n === 1 ? 'day' : 'days'} before due</span>
                  <button onClick={() => removeBefore(n)} className="btn-ghost" style={{ fontSize: 11, padding: '4px 10px', display: 'flex', alignItems: 'center', gap: 4 }}>
                    <X style={{ width: 12, height: 12 }} /> Remove
                  </button>
                </div>
              ))}
            </div>
            <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
              <input type="number" min="1" value={newBefore} onChange={e => setNewBefore(e.target.value)}
                placeholder="Days"
                onKeyDown={e => { if (e.key === 'Enter') addBefore() }}
                style={{ ...inp, width: 100 }}
                onFocus={e => e.currentTarget.style.borderColor = 'var(--focus-ring)'}
                onBlur={e => e.currentTarget.style.borderColor = 'var(--border-default)'} />
              <button onClick={addBefore} className="btn-ghost" style={{ fontSize: 12, padding: '7px 12px', display: 'flex', alignItems: 'center', gap: 5 }}>
                <Plus style={{ width: 13, height: 13 }} /> Add reminder
              </button>
            </div>
          </div>

          {/* After the due date */}
          <div>
            <p style={groupLabel}>After the due date</p>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 6, marginBottom: 10 }}>
              {config.days_after_due.length === 0 ? (
                <div style={emptyHint}>No reminders scheduled — add one below.</div>
              ) : config.days_after_due.map(n => (
                <div key={n} style={offsetRow}>
                  <span style={{ fontSize: 13, color: 'var(--ink-secondary)' }}>
                    {n === 0 ? 'On the due date' : `${n} ${n === 1 ? 'day' : 'days'} after due`}
                  </span>
                  <button onClick={() => removeAfter(n)} className="btn-ghost" style={{ fontSize: 11, padding: '4px 10px', display: 'flex', alignItems: 'center', gap: 4 }}>
                    <X style={{ width: 12, height: 12 }} /> Remove
                  </button>
                </div>
              ))}
            </div>
            <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
              <input type="number" min="0" value={newAfter} onChange={e => setNewAfter(e.target.value)}
                placeholder="Days (0 = due date)"
                onKeyDown={e => { if (e.key === 'Enter') addAfter() }}
                style={{ ...inp, width: 160 }}
                onFocus={e => e.currentTarget.style.borderColor = 'var(--focus-ring)'}
                onBlur={e => e.currentTarget.style.borderColor = 'var(--border-default)'} />
              <button onClick={addAfter} className="btn-ghost" style={{ fontSize: 12, padding: '7px 12px', display: 'flex', alignItems: 'center', gap: 5 }}>
                <Plus style={{ width: 13, height: 13 }} /> Add reminder
              </button>
            </div>
          </div>
        </div>

        {/* Save row */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginTop: 22, paddingTop: 18, borderTop: '1px solid var(--border-subtle)' }}>
          <button onClick={handleSave} disabled={saving || !dirty} className="btn-primary" style={{ width: 'fit-content' }}>
            <Save style={{ width: 14, height: 14 }} /> {saving ? 'Saving...' : 'Save reminder settings'}
          </button>
          {dirty && !saving && (
            <span style={{ fontSize: 12, color: 'var(--warning)' }}>You have unsaved changes</span>
          )}
        </div>
      </Section>
    </div>
  )
}

// ─── Terminology Settings ─────────────────────────────────────────────────────
function TerminologySettings() {
  const { workspace } = useAuth()
  const queryClient = useQueryClient()
  const [terms, setTerms] = useState({
    contact_singular: 'Contact', contact_plural: 'Contacts',
    deal_singular: 'Deal', deal_plural: 'Deals',
    request_singular: 'Request', request_plural: 'Requests',
    agent_singular: 'Agent', agent_plural: 'Agents',
  })
  const [saving, setSaving] = useState(false)

  const PRESETS = [
    { name: 'Default', values: { contact_singular: 'Contact', contact_plural: 'Contacts', deal_singular: 'Deal', deal_plural: 'Deals', request_singular: 'Request', request_plural: 'Requests', agent_singular: 'Agent', agent_plural: 'Agents' } },
    { name: 'Field service', values: { contact_singular: 'Customer', contact_plural: 'Customers', deal_singular: 'Quote', deal_plural: 'Quotes', request_singular: 'Job', request_plural: 'Jobs', agent_singular: 'Technician', agent_plural: 'Technicians' } },
    { name: 'Consulting', values: { contact_singular: 'Client', contact_plural: 'Clients', deal_singular: 'Opportunity', deal_plural: 'Opportunities', request_singular: 'Ticket', request_plural: 'Tickets', agent_singular: 'Consultant', agent_plural: 'Consultants' } },
    { name: 'Real estate', values: { contact_singular: 'Lead', contact_plural: 'Leads', deal_singular: 'Listing', deal_plural: 'Listings', request_singular: 'Inquiry', request_plural: 'Inquiries', agent_singular: 'Agent', agent_plural: 'Agents' } },
  ]

  const TERM_FIELDS = [
    { key: 'contact', label: 'Contacts' },
    { key: 'deal', label: 'Deals' },
    { key: 'request', label: 'Requests / Tickets' },
    { key: 'agent', label: 'Team members' },
  ]

  const handleSave = async () => {
    setSaving(true)
    await new Promise(r => setTimeout(r, 600))
    setSaving(false)
    toast.success('Terminology saved')
  }

  return (
    <div>
      <Section title="Terminology" description="Rename modules to match your industry's language">
        <div style={{ marginBottom: 20 }}>
          <p style={{ fontSize: 12, color: 'var(--ink-tertiary)', margin: '0 0 10px' }}>Quick presets:</p>
          <div style={{ display: 'flex', gap: 8 }}>
            {PRESETS.map(preset => (
              <button key={preset.name} onClick={() => setTerms(preset.values as any)}
                style={{ padding: '7px 14px', borderRadius: 9, fontSize: 12, fontWeight: 600, cursor: 'pointer', background: 'var(--hover-bg)', border: '1px solid var(--border-default)', color: 'var(--ink-secondary)', transition: 'all 150ms ease' }}
                onMouseEnter={e => { e.currentTarget.style.background = 'var(--accent-dim)'; e.currentTarget.style.borderColor = 'var(--accent-border)'; e.currentTarget.style.color = 'var(--accent-text)' }}
                onMouseLeave={e => { e.currentTarget.style.background = 'var(--hover-bg)'; e.currentTarget.style.borderColor = 'var(--border-default)'; e.currentTarget.style.color = 'var(--ink-secondary)' }}>
                {preset.name}
              </button>
            ))}
          </div>
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
          {TERM_FIELDS.map(field => (
            <div key={field.key}>
              <label style={{ fontSize: 11, fontWeight: 600, color: 'var(--ink-tertiary)', display: 'block', marginBottom: 8, textTransform: 'uppercase', letterSpacing: '0.06em' }}>{field.label}</label>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
                <div>
                  <p style={{ fontSize: 10, color: 'var(--ink-tertiary)', margin: '0 0 5px' }}>Singular</p>
                  <input value={(terms as any)[`${field.key}_singular`]}
                    onChange={e => setTerms(p => ({ ...p, [`${field.key}_singular`]: e.target.value }))}
                    style={inp}
                    onFocus={e => e.currentTarget.style.borderColor = 'var(--focus-ring)'}
                    onBlur={e => e.currentTarget.style.borderColor = 'var(--border-default)'} />
                </div>
                <div>
                  <p style={{ fontSize: 10, color: 'var(--ink-tertiary)', margin: '0 0 5px' }}>Plural</p>
                  <input value={(terms as any)[`${field.key}_plural`]}
                    onChange={e => setTerms(p => ({ ...p, [`${field.key}_plural`]: e.target.value }))}
                    style={inp}
                    onFocus={e => e.currentTarget.style.borderColor = 'var(--focus-ring)'}
                    onBlur={e => e.currentTarget.style.borderColor = 'var(--border-default)'} />
                </div>
              </div>
            </div>
          ))}
          <button onClick={handleSave} disabled={saving} className="btn-primary" style={{ width: 'fit-content' }}>
            <Save style={{ width: 14, height: 14 }} /> {saving ? 'Saving...' : 'Save terminology'}
          </button>
        </div>
      </Section>
    </div>
  )
}

// ─── Notifications Settings ───────────────────────────────────────────────────
const NOTIFICATION_DEFAULTS: Record<string, boolean> = {
  new_conversation: true, conversation_assigned: true, new_message: true,
  new_review: true, review_response_needed: false,
  job_scheduled: true, job_complete: true, job_overdue: true,
  invoice_paid: true, invoice_overdue: true,
  deal_won: true, deal_lost: false,
}

function NotificationsSettings() {
  const { profile } = useAuth()
  const queryClient = useQueryClient()
  const [settings, setSettings] = useState<Record<string, boolean>>(NOTIFICATION_DEFAULTS)
  const [saving, setSaving] = useState(false)

  // Load saved prefs; merge over defaults so future toggles default sensibly
  // when the schema is extended without a per-user migration.
  const { data: savedPrefs } = useQuery({
    queryKey: ['notification-preferences', profile?.id],
    queryFn: async () => {
      if (!profile?.id) return null
      // .maybeSingle (not .single) — returns {data:null} for 0 rows
      // instead of HTTP 406. Defends against transient PostgREST schema-
      // cache lag (the same 406 mode that bit useCalendarLabelPrefs after
      // its migration). null flows through the ?? {} below into defaults.
      const { data } = await supabase
        .from('profiles')
        .select('notification_preferences')
        .eq('id', profile.id)
        .maybeSingle()
      return (data?.notification_preferences ?? {}) as Record<string, boolean>
    },
    enabled: !!profile?.id,
  })

  useEffect(() => {
    if (savedPrefs) setSettings({ ...NOTIFICATION_DEFAULTS, ...savedPrefs })
  }, [savedPrefs])

  const toggle = (key: string) => setSettings(p => ({ ...p, [key]: !p[key] }))

  const handleSave = async () => {
    if (!profile?.id) { toast.error('Not signed in'); return }
    setSaving(true)
    const { error } = await supabase
      .from('profiles')
      .update({ notification_preferences: settings })
      .eq('id', profile.id)
    setSaving(false)
    if (error) {
      toast.error(`Failed to save: ${error.message}`)
      return
    }
    queryClient.invalidateQueries({ queryKey: ['notification-preferences', profile.id] })
    toast.success('Notification preferences saved')
  }

  const GROUPS = [
    {
      label: 'Inbox', items: [
        { key: 'new_conversation', label: 'New conversation' },
        { key: 'conversation_assigned', label: 'Conversation assigned to me' },
        { key: 'new_message', label: 'New message in my conversations' },
      ]
    },
    {
      label: 'Reputation', items: [
        { key: 'new_review', label: 'New review received' },
        { key: 'review_response_needed', label: 'Review needs response (negative)' },
      ]
    },
    {
      label: 'Field Hub', items: [
        { key: 'job_scheduled', label: 'New job scheduled' },
        { key: 'job_complete', label: 'Job marked complete' },
        { key: 'job_overdue', label: 'Job is overdue' },
      ]
    },
    {
      label: 'Payments', items: [
        { key: 'invoice_paid', label: 'Invoice paid' },
        { key: 'invoice_overdue', label: 'Invoice overdue' },
      ]
    },
    {
      label: 'CRM', items: [
        { key: 'deal_won', label: 'Deal marked won' },
        { key: 'deal_lost', label: 'Deal marked lost' },
      ]
    },
  ]

  return (
    <div>
      <Section title="Notification preferences" description="Choose what you get notified about">
        {GROUPS.map(group => (
          <div key={group.label} style={{ marginBottom: 20 }}>
            <p style={{ fontSize: 11, fontWeight: 700, color: 'var(--ink-tertiary)', textTransform: 'uppercase', letterSpacing: '0.08em', margin: '0 0 10px' }}>{group.label}</p>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
              {group.items.map(item => (
                <div key={item.key} onClick={() => toggle(item.key)}
                  style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '11px 14px', background: 'var(--surface)', border: '1px solid var(--border-subtle)', borderRadius: 10, cursor: 'pointer', transition: 'background 150ms ease' }}
                  onMouseEnter={e => e.currentTarget.style.background = 'var(--hover-bg)'}
                  onMouseLeave={e => e.currentTarget.style.background = 'var(--surface)'}>
                  <span style={{ fontSize: 13, color: 'var(--ink-secondary)' }}>{item.label}</span>
                  <div style={{ width: 36, height: 20, borderRadius: 100, background: settings[item.key] ? 'var(--accent)' : 'var(--border-default)', transition: 'background 200ms ease', position: 'relative', flexShrink: 0 }}>
                    <div style={{ position: 'absolute', top: 3, left: settings[item.key] ? 19 : 3, width: 14, height: 14, borderRadius: '50%', background: 'var(--accent-ink)', transition: 'left 200ms ease', boxShadow: 'var(--shadow-knob)' }} />
                  </div>
                </div>
              ))}
            </div>
          </div>
        ))}
        <button onClick={handleSave} disabled={saving} className="btn-primary" style={{ width: 'fit-content', marginTop: 4 }}>
          <Save style={{ width: 14, height: 14 }} /> {saving ? 'Saving...' : 'Save preferences'}
        </button>
      </Section>
    </div>
  )
}

// ─── Appearance Settings ──────────────────────────────────────────────────────
// Theme picker (light / dark / sepia / system). Selecting an option calls
// setTheme from useTheme — that writes to localStorage and profiles.appearance
// in one go (optimistic; DB write is fire-and-forget). 'system' resolves at
// runtime to light or dark based on prefers-color-scheme; sepia is never the
// auto-resolved choice.
function AppearanceSettings() {
  const { theme, resolvedTheme, setTheme } = useTheme()

  const OPTIONS: { value: Theme; label: string; icon: React.ComponentType<any>; description: string }[] = [
    { value: 'light',  label: 'Light',  icon: Sun,      description: 'Clean neutral surface.' },
    { value: 'dark',   label: 'Dark',   icon: Moon,     description: 'High-contrast space.' },
    { value: 'sepia',  label: 'Sepia',  icon: BookOpen, description: 'Warm paper, eased on the eyes.' },
    { value: 'system', label: 'System', icon: Monitor,  description: `Follow your OS — currently ${resolvedTheme}.` },
  ]

  return (
    <div>
      <Section title="Theme" description="Pick how Orkestra looks on this account. Your choice syncs across devices.">
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 10 }}>
          {OPTIONS.map(opt => {
            const Icon = opt.icon
            const isActive = theme === opt.value
            return (
              <button
                key={opt.value}
                onClick={() => setTheme(opt.value)}
                data-testid={`appearance-option-${opt.value}`}
                style={{
                  display: 'flex', alignItems: 'flex-start', gap: 12,
                  padding: '14px 16px', textAlign: 'left',
                  background: isActive ? 'var(--accent-dim)' : 'var(--surface)',
                  border: `1px solid ${isActive ? 'var(--accent-border)' : 'var(--border-subtle)'}`,
                  borderRadius: 12, cursor: 'pointer',
                  transition: 'background 150ms ease, border-color 150ms ease',
                }}
                onMouseEnter={e => { if (!isActive) e.currentTarget.style.background = 'var(--hover-bg)' }}
                onMouseLeave={e => { if (!isActive) e.currentTarget.style.background = 'var(--surface)' }}
              >
                <div style={{
                  width: 36, height: 36, borderRadius: 10,
                  background: isActive ? 'var(--accent)' : 'var(--field-bg)',
                  border: `1px solid ${isActive ? 'var(--accent-border)' : 'var(--border-subtle)'}`,
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  flexShrink: 0,
                }}>
                  <Icon style={{ width: 16, height: 16, color: isActive ? 'var(--accent-ink)' : 'var(--ink-secondary)' }} />
                </div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 3 }}>
                    <span style={{ fontSize: 13, fontWeight: 700, color: 'var(--ink-primary)' }}>{opt.label}</span>
                    {isActive && <Check style={{ width: 13, height: 13, color: 'var(--accent-text)' }} />}
                  </div>
                  <p style={{ fontSize: 12, color: 'var(--ink-tertiary)', margin: 0, lineHeight: 1.4 }}>{opt.description}</p>
                </div>
              </button>
            )
          })}
        </div>
      </Section>
    </div>
  )
}

// ─── Security Settings ────────────────────────────────────────────────────────
function SecuritySettings() {
  const { profile } = useAuth()
  const [currentPassword, setCurrentPassword] = useState('')
  const [newPassword, setNewPassword] = useState('')
  const [confirmPassword, setConfirmPassword] = useState('')
  const [saving, setSaving] = useState(false)

  const handleChangePassword = async () => {
    if (!newPassword) { toast.error('Enter a new password'); return }
    if (newPassword.length < 8) { toast.error('Password must be at least 8 characters'); return }
    if (newPassword !== confirmPassword) { toast.error('Passwords do not match'); return }
    setSaving(true)
    const { error } = await supabase.auth.updateUser({ password: newPassword })
    setSaving(false)
    if (error) { toast.error(error.message); return }
    toast.success('Password updated')
    setCurrentPassword(''); setNewPassword(''); setConfirmPassword('')
  }

  return (
    <div>
      <Section title="Change password" description="Update your account password">
        <div style={{ display: 'flex', flexDirection: 'column', gap: 14, maxWidth: 400 }}>
          <div>
            <label style={{ fontSize: 11, fontWeight: 600, color: 'var(--ink-tertiary)', display: 'block', marginBottom: 6, textTransform: 'uppercase', letterSpacing: '0.06em' }}>New password</label>
            <input type="password" value={newPassword} onChange={e => setNewPassword(e.target.value)} placeholder="8+ characters" style={inp}
              onFocus={e => e.currentTarget.style.borderColor = 'var(--focus-ring)'}
              onBlur={e => e.currentTarget.style.borderColor = 'var(--border-default)'} />
          </div>
          <div>
            <label style={{ fontSize: 11, fontWeight: 600, color: 'var(--ink-tertiary)', display: 'block', marginBottom: 6, textTransform: 'uppercase', letterSpacing: '0.06em' }}>Confirm new password</label>
            <input type="password" value={confirmPassword} onChange={e => setConfirmPassword(e.target.value)} placeholder="Repeat new password" style={inp}
              onFocus={e => e.currentTarget.style.borderColor = 'var(--focus-ring)'}
              onBlur={e => e.currentTarget.style.borderColor = 'var(--border-default)'} />
          </div>
          <button onClick={handleChangePassword} disabled={saving} className="btn-primary" style={{ width: 'fit-content' }}>
            {saving ? 'Updating...' : 'Update password'}
          </button>
        </div>
      </Section>

      <Section title="Sessions" description="Manage your active sessions">
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '12px 14px', background: 'var(--surface)', border: '1px solid var(--border-subtle)', borderRadius: 12 }}>
          <div>
            <p style={{ fontSize: 13, fontWeight: 600, color: 'var(--ink-primary)', margin: '0 0 2px' }}>Current session</p>
            <p style={{ fontSize: 11, color: 'var(--ink-tertiary)', margin: 0 }}>This browser · Active now</p>
          </div>
          <div className="live-dot" />
        </div>
      </Section>
    </div>
  )
}

// ─── Audit Log (read-only viewer over audit_events) ──────────────────────────
// Owner/admin-gated. Pure read — audit_events is append-only (trigger-written) and
// RLS (audit_events_select_workspace) already scopes the read to the workspace.
const AUDIT_PAGE = 50
const AUDIT_SYSTEM_KEYS = new Set(['id', 'workspace_id', 'created_at', 'updated_at'])
const _auditActionLabel = (a: string) => a.replace(/_/g, ' ').replace(/^\w/, c => c.toUpperCase())
const _auditEntityLabel = (t: string) => (({ field_jobs: 'Job', invoices: 'Invoice', quotes: 'Quote' } as Record<string, string>)[t] || t)
const _auditEntityPlural = (t: string) => _auditEntityLabel(t).toLowerCase() + 's'
const _auditRef = (after: any, before: any) => {
  const o = (after && Object.keys(after).length ? after : before) || {}
  return o.invoice_number || o.quote_number || o.title || o.name || (o.id ? String(o.id).slice(0, 8) : '')
}
const _auditVal = (v: any) => {
  if (v === null || v === undefined) return '∅'
  const s = typeof v === 'object' ? JSON.stringify(v) : String(v)
  return s.length > 60 ? s.slice(0, 57) + '…' : s
}
function _auditDiff(before: any, after: any): { key: string; from: any; to: any }[] {
  const b = before || {}, a = after || {}
  const out: { key: string; from: any; to: any }[] = []
  for (const k of new Set([...Object.keys(b), ...Object.keys(a)])) {
    if (AUDIT_SYSTEM_KEYS.has(k)) continue
    if (JSON.stringify(b[k]) !== JSON.stringify(a[k])) out.push({ key: k, from: b[k], to: a[k] })
  }
  return out
}

function AuditLogSettings() {
  const { profile, workspace } = useAuth()
  const wsId = workspace?.id ?? null
  const isOwnerAdmin = !!profile?.is_owner || profile?.role === 'admin'
  const [page, setPage] = useState(0)
  const [entityFilter, setEntityFilter] = useState<string>('all')
  const [expanded, setExpanded] = useState<string | null>(null)

  // distinct entity types → coverage note + filter chips (data-derived, not hardcoded)
  const { data: entityTypes = [] } = useQuery({
    queryKey: ['audit-entity-types', wsId],
    queryFn: async () => {
      const { data } = await (supabase as any).from('audit_events').select('entity_type').eq('workspace_id', wsId)
      return Array.from(new Set(((data || []) as any[]).map(r => r.entity_type))).sort()
    },
    enabled: !!wsId && isOwnerAdmin,
  })

  const { data: events = [], isLoading } = useQuery({
    queryKey: ['audit-events', wsId, page, entityFilter],
    queryFn: async () => {
      let q = (supabase as any).from('audit_events').select('*').eq('workspace_id', wsId)
        .order('created_at', { ascending: false })
        .range(page * AUDIT_PAGE, page * AUDIT_PAGE + AUDIT_PAGE - 1)
      if (entityFilter !== 'all') q = q.eq('entity_type', entityFilter)
      const { data } = await q
      return (data || []) as any[]
    },
    enabled: !!wsId && isOwnerAdmin,
  })

  if (!isOwnerAdmin) {
    return (
      <div>
        <Section title="Audit log" description="Who changed what, and when.">
          <p style={{ fontSize: 13, color: 'var(--ink-tertiary)', margin: 0 }}>Only owners and admins can view the audit log.</p>
        </Section>
      </div>
    )
  }

  const coverage = (entityTypes as string[]).length
    ? (entityTypes as string[]).map(_auditEntityPlural).join(', ')
    : null
  const GRID = '1fr 1.4fr 1.1fr 1.6fr'

  return (
    <div>
      <Section title="Audit log" description="Who changed what, and when.">
        {coverage && (
          <p style={{ fontSize: 12, color: 'var(--ink-tertiary)', margin: '0 0 14px' }}>Tracking changes to {coverage}.</p>
        )}

        {/* entity-type filter chips (derived from the data) */}
        <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginBottom: 14 }}>
          {['all', ...(entityTypes as string[])].map(t => {
            const active = entityFilter === t
            return (
              <button key={t} type="button" onClick={() => { setEntityFilter(t); setPage(0); setExpanded(null) }}
                style={{ padding: '6px 12px', borderRadius: 8, fontSize: 12, fontWeight: 600, cursor: 'pointer', background: active ? 'var(--accent)' : 'var(--hover-bg)', color: active ? 'var(--accent-ink)' : 'var(--ink-tertiary)', border: active ? 'none' : '1px solid var(--border-subtle)' }}>
                {t === 'all' ? 'All' : _auditEntityLabel(t)}
              </button>
            )
          })}
        </div>

        {isLoading ? (
          <p style={{ fontSize: 13, color: 'var(--ink-tertiary)', margin: 0 }}>Loading…</p>
        ) : events.length === 0 ? (
          <p style={{ fontSize: 13, color: 'var(--ink-tertiary)', margin: 0 }}>{page === 0 ? 'No audit events yet.' : 'No more events.'}</p>
        ) : (
          <div style={{ border: '1px solid var(--border-subtle)', borderRadius: 10, overflow: 'hidden', background: 'var(--hover-bg)' }}>
            <div style={{ display: 'grid', gridTemplateColumns: GRID, gap: 12, padding: '8px 12px', fontSize: 10, fontWeight: 700, color: 'var(--ink-tertiary)', textTransform: 'uppercase', letterSpacing: '0.06em', borderBottom: '1px solid var(--border-subtle)' }}>
              <span>When</span><span>Who</span><span>Action</span><span>Record</span>
            </div>
            {events.map(e => {
              const created = !e.before || Object.keys(e.before).length === 0
              const deleted = !e.after || Object.keys(e.after).length === 0
              const ref = _auditRef(e.after, e.before)
              const open = expanded === e.id
              const diffs = _auditDiff(e.before, e.after)
              return (
                <div key={e.id} style={{ borderTop: '1px solid var(--border-subtle)' }}>
                  <div onClick={() => setExpanded(open ? null : e.id)}
                    style={{ display: 'grid', gridTemplateColumns: GRID, gap: 12, padding: '10px 12px', alignItems: 'center', fontSize: 13, cursor: 'pointer' }}
                    onMouseEnter={ev => { ev.currentTarget.style.background = 'var(--surface)' }}
                    onMouseLeave={ev => { ev.currentTarget.style.background = 'transparent' }}>
                    <span style={{ color: 'var(--ink-tertiary)', fontSize: 12 }}>{formatRelativeDate(e.created_at)}</span>
                    <span style={{ color: 'var(--ink-secondary)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{e.actor_email || (e.actor_type === 'system' ? 'System' : '—')}</span>
                    <span>
                      <span style={{ fontSize: 11, fontWeight: 600, padding: '2px 8px', borderRadius: 100, background: 'var(--accent-dim)', color: 'var(--accent-text)' }}>{_auditActionLabel(e.action)}</span>
                    </span>
                    <span style={{ color: 'var(--ink-primary)', fontWeight: 600, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {_auditEntityLabel(e.entity_type)}{ref ? <span style={{ color: 'var(--ink-tertiary)', fontWeight: 400 }}> · {ref}</span> : null}
                    </span>
                  </div>
                  {open && (
                    <div style={{ padding: '0 12px 12px 12px', fontSize: 12, color: 'var(--ink-secondary)' }}>
                      {created ? (
                        <p style={{ margin: 0, color: 'var(--ink-tertiary)' }}>Created — {ref || _auditEntityLabel(e.entity_type)}</p>
                      ) : deleted ? (
                        <p style={{ margin: 0, color: 'var(--ink-tertiary)' }}>Deleted — {ref || _auditEntityLabel(e.entity_type)}</p>
                      ) : diffs.length === 0 ? (
                        <p style={{ margin: 0, color: 'var(--ink-tertiary)' }}>No field changes recorded.</p>
                      ) : (
                        <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                          {diffs.map(d => (
                            <div key={d.key} style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                              <span style={{ fontWeight: 600, color: 'var(--ink-primary)' }}>{d.key}:</span>
                              <span style={{ color: 'var(--ink-tertiary)' }}>{_auditVal(d.from)}</span>
                              <span style={{ color: 'var(--ink-tertiary)' }}>→</span>
                              <span style={{ color: 'var(--ink-primary)' }}>{_auditVal(d.to)}</span>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  )}
                </div>
              )
            })}
          </div>
        )}

        {/* pagination */}
        {(page > 0 || events.length === AUDIT_PAGE) && (
          <div style={{ display: 'flex', gap: 8, marginTop: 14, justifyContent: 'flex-end' }}>
            <button type="button" disabled={page === 0} onClick={() => { setPage(p => Math.max(0, p - 1)); setExpanded(null) }}
              style={{ padding: '7px 12px', background: 'var(--hover-bg)', border: '1px solid var(--border-subtle)', borderRadius: 8, fontSize: 12, fontWeight: 600, color: 'var(--ink-secondary)', cursor: page === 0 ? 'not-allowed' : 'pointer', opacity: page === 0 ? 0.5 : 1 }}>Previous</button>
            <button type="button" disabled={events.length < AUDIT_PAGE} onClick={() => { setPage(p => p + 1); setExpanded(null) }}
              style={{ padding: '7px 12px', background: 'var(--hover-bg)', border: '1px solid var(--border-subtle)', borderRadius: 8, fontSize: 12, fontWeight: 600, color: 'var(--ink-secondary)', cursor: events.length < AUDIT_PAGE ? 'not-allowed' : 'pointer', opacity: events.length < AUDIT_PAGE ? 0.5 : 1 }}>Next</button>
          </div>
        )}
      </Section>
    </div>
  )
}

// ─── Pipeline Settings (win probability per stage) ────────────────────────────
// Owner/admin-gated (UI only — RLS workspace_isolation already permits the
// UPDATE for any member). Edits win_probability per pipeline_stage; the weighted
// forecast (/app/reports) reads this, so we invalidate its stage key too.
// Default stage set for a NEW pipeline (mirrors the signup seed's win-probability
// ramp). Colors are omitted (null) → the kanban/editor fall back to var(--accent);
// avoids new hex literals and colors aren't editable in this CP anyway.
const PIPELINE_DEFAULT_STAGES = [
  { name: 'Lead', order_index: 0, win_probability: 10 },
  { name: 'Qualified', order_index: 1, win_probability: 25 },
  { name: 'Proposal', order_index: 2, win_probability: 50 },
  { name: 'Negotiation', order_index: 3, win_probability: 75 },
  { name: 'Won', order_index: 4, win_probability: 100 },
  { name: 'Lost', order_index: 5, win_probability: 0 },
]

function PipelineSettings() {
  const { workspace, profile } = useAuth()
  const queryClient = useQueryClient()
  const wsId = workspace?.id ?? null
  const isOwnerAdmin = !!profile?.is_owner || profile?.role === 'admin'
  const [selectedPid, setSelectedPid] = useState<string>('')

  const { data: pipelines = [] } = useQuery({
    queryKey: ['pipelines', wsId],
    queryFn: async () => {
      if (!wsId) return []
      const { data } = await (supabase as any).from('pipelines')
        .select('id,name,is_default').eq('workspace_id', wsId)
        .order('is_default', { ascending: false }).order('created_at')
      return data || []
    },
    enabled: !!wsId && isOwnerAdmin,
  })

  // Effective selection: explicit pick → else the default → else the first. No
  // useEffect needed (derived), so it self-corrects as pipelines load/change.
  const effectivePid = selectedPid || (pipelines.find((p: any) => p.is_default)?.id) || (pipelines[0]?.id) || ''
  const activePipeline = pipelines.find((p: any) => p.id === effectivePid)

  const { data: stages = [], isLoading } = useQuery({
    queryKey: ['pipeline-stages', effectivePid],
    queryFn: async () => {
      if (!effectivePid) return []
      const { data, error } = await (supabase as any).from('pipeline_stages')
        .select('id,name,order_index,win_probability')
        .eq('pipeline_id', effectivePid).order('order_index')
      if (error) throw error
      return data || []
    },
    enabled: !!effectivePid && isOwnerAdmin,
  })

  // Deal count on the active pipeline — gates delete (can't delete a pipeline with deals).
  const { data: dealCount = 0 } = useQuery({
    queryKey: ['pipeline-deal-count', effectivePid],
    queryFn: async () => {
      if (!effectivePid) return 0
      const { count } = await (supabase as any).from('deals').select('id', { count: 'exact', head: true }).eq('pipeline_id', effectivePid)
      return count || 0
    },
    enabled: !!effectivePid && isOwnerAdmin,
  })

  const invalidateAll = () => {
    queryClient.invalidateQueries({ queryKey: ['pipelines'] })
    queryClient.invalidateQueries({ queryKey: ['pipeline-stages'] })
    queryClient.invalidateQueries({ queryKey: ['forecast-stages'] })
  }

  const createPipeline = useMutation({
    mutationFn: async (name: string) => {
      const { data: pipe, error } = await (supabase as any).from('pipelines')
        .insert({ workspace_id: wsId, name, is_default: false }).select('id').single()
      if (error) throw error
      const payload = PIPELINE_DEFAULT_STAGES.map(s => ({ ...s, pipeline_id: pipe.id, workspace_id: wsId }))
      const { error: se } = await (supabase as any).from('pipeline_stages').insert(payload)
      if (se) throw se
      return pipe.id as string
    },
    onSuccess: (id) => { invalidateAll(); setSelectedPid(id); toast.success('Pipeline created') },
    onError: (e: any) => toast.error(`Create failed: ${e.message}`),
  })
  const renamePipeline = useMutation({
    mutationFn: async ({ id, name }: { id: string; name: string }) => {
      const { error } = await (supabase as any).from('pipelines').update({ name }).eq('id', id)
      if (error) throw error
    },
    onSuccess: () => { invalidateAll(); toast.success('Pipeline renamed') },
    onError: (e: any) => toast.error(`Rename failed: ${e.message}`),
  })
  const setDefaultPipeline = useMutation({
    mutationFn: async (id: string) => {
      // exactly one default: clear the others, then set this one
      const { error: e1 } = await (supabase as any).from('pipelines').update({ is_default: false }).eq('workspace_id', wsId).neq('id', id)
      if (e1) throw e1
      const { error: e2 } = await (supabase as any).from('pipelines').update({ is_default: true }).eq('id', id)
      if (e2) throw e2
    },
    onSuccess: () => { invalidateAll(); toast.success('Default pipeline set') },
    onError: (e: any) => toast.error(`Failed: ${e.message}`),
  })
  const deletePipeline = useMutation({
    mutationFn: async (id: string) => {
      const { error } = await (supabase as any).from('pipelines').delete().eq('id', id)  // stages cascade (FK)
      if (error) throw error
    },
    onSuccess: () => { invalidateAll(); setSelectedPid(''); toast.success('Pipeline deleted') },
    onError: (e: any) => toast.error(`Delete failed: ${e.message}`),
  })

  const updateMutation = useMutation({
    mutationFn: async ({ id, win }: { id: string; win: number }) => {
      const { error } = await (supabase as any).from('pipeline_stages').update({ win_probability: win }).eq('id', id)
      if (error) throw error
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['pipeline-stages'] })
      queryClient.invalidateQueries({ queryKey: ['forecast-stages'] })
      toast.success('Win probability saved')
    },
    onError: (e: any) => toast.error(`Save failed: ${e.message}`),
  })

  if (!isOwnerAdmin) {
    return (
      <div>
        <Section title="Pipeline" description="Win probability per pipeline stage.">
          <p style={{ fontSize: 13, color: 'var(--ink-tertiary)', margin: 0 }}>Only owners and admins can manage pipelines.</p>
        </Section>
      </div>
    )
  }

  const canDelete = !!activePipeline && !activePipeline.is_default && dealCount === 0 && pipelines.length > 1
  const deleteReason = !activePipeline ? '' : activePipeline.is_default ? 'Default pipeline can’t be deleted' : pipelines.length <= 1 ? 'Can’t delete the only pipeline' : dealCount > 0 ? `${dealCount} deal(s) use this pipeline` : ''

  return (
    <div>
      <Section title="Pipelines" description="Manage your deal pipelines and the win probability of each stage. The weighted forecast multiplies each open deal’s value by its stage’s probability.">
        {/* pipeline selector + create */}
        <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', alignItems: 'center', marginBottom: 14 }}>
          {(pipelines as any[]).map(p => {
            const active = p.id === effectivePid
            return (
              <button key={p.id} type="button" onClick={() => setSelectedPid(p.id)}
                style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '7px 12px', borderRadius: 8, fontSize: 12, fontWeight: 600, cursor: 'pointer', background: active ? 'var(--accent)' : 'var(--hover-bg)', color: active ? 'var(--accent-ink)' : 'var(--ink-tertiary)', border: active ? 'none' : '1px solid var(--border-subtle)' }}>
                {p.name}
                {p.is_default && <span style={{ fontSize: 9, fontWeight: 700, padding: '1px 5px', borderRadius: 100, background: active ? 'var(--accent-dim)' : 'var(--border-subtle)', color: active ? 'var(--accent-text)' : 'var(--ink-tertiary)', textTransform: 'uppercase' }}>Default</span>}
              </button>
            )
          })}
          <button type="button" disabled={createPipeline.isPending}
            onClick={() => { const n = window.prompt('New pipeline name')?.trim(); if (n) createPipeline.mutate(n) }}
            style={{ display: 'flex', alignItems: 'center', gap: 4, padding: '7px 12px', borderRadius: 8, fontSize: 12, fontWeight: 600, cursor: 'pointer', background: 'none', border: '1px dashed var(--border-default)', color: 'var(--ink-tertiary)' }}>
            <Plus style={{ width: 12, height: 12 }} /> New pipeline
          </button>
        </div>

        {/* active-pipeline actions */}
        {activePipeline && (
          <div style={{ display: 'flex', gap: 14, alignItems: 'center', marginBottom: 14, fontSize: 12 }}>
            <button type="button" onClick={() => { const n = window.prompt('Rename pipeline', activePipeline.name)?.trim(); if (n && n !== activePipeline.name) renamePipeline.mutate({ id: activePipeline.id, name: n }) }}
              style={{ background: 'none', border: 'none', color: 'var(--accent-text)', fontWeight: 600, cursor: 'pointer', padding: 0 }}>Rename</button>
            {!activePipeline.is_default && (
              <button type="button" onClick={() => setDefaultPipeline.mutate(activePipeline.id)}
                style={{ background: 'none', border: 'none', color: 'var(--accent-text)', fontWeight: 600, cursor: 'pointer', padding: 0 }}>Make default</button>
            )}
            <button type="button" disabled={!canDelete} title={canDelete ? '' : deleteReason}
              onClick={() => { if (canDelete && window.confirm(`Delete pipeline “${activePipeline.name}” and its stages?`)) deletePipeline.mutate(activePipeline.id) }}
              style={{ background: 'none', border: 'none', color: canDelete ? 'var(--danger)' : 'var(--ink-tertiary)', fontWeight: 600, cursor: canDelete ? 'pointer' : 'not-allowed', padding: 0 }}>
              Delete{!canDelete && deleteReason ? ` · ${deleteReason}` : ''}
            </button>
          </div>
        )}

        {/* stage win-probability editor — scoped to the selected pipeline */}
        {isLoading ? (
          <p style={{ fontSize: 13, color: 'var(--ink-tertiary)', margin: 0 }}>Loading…</p>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            {stages.map((s: any) => (
              <div key={s.id}
                style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '12px 14px', background: 'var(--surface)', border: '1px solid var(--border-subtle)', borderRadius: 10 }}>
                <span style={{ flex: 1, minWidth: 0, fontSize: 13, fontWeight: 600, color: 'var(--ink-primary)' }}>{s.name}</span>
                <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                  <input
                    key={`${s.id}-${s.win_probability}`}
                    type="number" min={0} max={100} step={5}
                    defaultValue={s.win_probability ?? 0}
                    onBlur={e => {
                      const current = s.win_probability ?? 0
                      const parsed = parseInt(e.target.value, 10)
                      if (Number.isNaN(parsed)) { e.target.value = String(current); return }
                      const clamped = Math.max(0, Math.min(100, parsed))
                      e.target.value = String(clamped)
                      if (clamped !== current) updateMutation.mutate({ id: s.id, win: clamped })
                    }}
                    onKeyDown={e => { if (e.key === 'Enter') (e.target as HTMLInputElement).blur() }}
                    onFocus={e => { e.currentTarget.style.borderColor = 'var(--focus-ring)' }}
                    style={{ width: 72, padding: '7px 10px', background: 'var(--field-bg)', border: '1px solid var(--border-default)', borderRadius: 8, fontSize: 13, color: 'var(--ink-primary)', outline: 'none', textAlign: 'right' }}
                  />
                  <span style={{ fontSize: 13, color: 'var(--ink-tertiary)', width: 12 }}>%</span>
                </div>
              </div>
            ))}
            {stages.length === 0 && <p style={{ fontSize: 13, color: 'var(--ink-tertiary)', margin: 0 }}>This pipeline has no stages.</p>}
          </div>
        )}
      </Section>
    </div>
  )
}

// ─── Service Lines Settings ───────────────────────────────────────────────────
function ServiceLinesSettings() {
  const { workspace } = useAuth()
  const queryClient = useQueryClient()
  const [showAdd, setShowAdd] = useState(false)
  const [form, setForm] = useState({ name: '', description: '' })

  const { data: serviceLines = [], isLoading } = useQuery({
    queryKey: ['service-lines', workspace?.id],
    queryFn: async () => {
      if (!workspace?.id) return []
      // sort_order is the operator-controlled ordering used by the
      // portal + NewJobModal picker; name is the tiebreaker for legacy
      // rows whose sort_order is still the alphabetical backfill.
      const { data, error } = await supabase.from('service_lines').select('*').eq('workspace_id', workspace.id).order('sort_order').order('name')
      if (error) throw error
      return data || []
    },
    enabled: !!workspace?.id,
  })

  const createMutation = useMutation({
    mutationFn: async (input: { name: string; description: string }) => {
      // Place new rows at the end of the list — next available
      // sort_order. Avoids reshuffling existing rows.
      const nextSort = (serviceLines.reduce((max: number, sl: any) => Math.max(max, sl.sort_order ?? 0), 0)) + 1
      const { error } = await supabase.from('service_lines').insert({
        name: input.name,
        description: input.description || null,
        workspace_id: workspace!.id,
        sort_order: nextSort,
      })
      if (error) throw error
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['service-lines'] })
      setForm({ name: '', description: '' })
      setShowAdd(false)
      toast.success('Service line added')
    },
    onError: (e: any) => toast.error(`Failed: ${e.message}`),
  })

  // Reorder by swapping sort_order between the row and its neighbor.
  // Two updates in flight — no transaction guarantee, but a brief
  // duplicate ordering is harmless (the secondary `name` order kicks
  // in deterministically). Failure rolls back via query invalidate.
  const reorderMutation = useMutation({
    mutationFn: async ({ id, direction }: { id: string; direction: 'up' | 'down' }) => {
      const sorted = [...serviceLines].sort((a: any, b: any) => (a.sort_order ?? 0) - (b.sort_order ?? 0))
      const idx = sorted.findIndex((sl: any) => sl.id === id)
      if (idx < 0) return
      const neighborIdx = direction === 'up' ? idx - 1 : idx + 1
      if (neighborIdx < 0 || neighborIdx >= sorted.length) return
      const self: any = sorted[idx]
      const neighbor: any = sorted[neighborIdx]
      await Promise.all([
        supabase.from('service_lines').update({ sort_order: neighbor.sort_order }).eq('id', self.id),
        supabase.from('service_lines').update({ sort_order: self.sort_order }).eq('id', neighbor.id),
      ])
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['service-lines'] }),
    onError: (e: any) => toast.error(`Reorder failed: ${e.message}`),
  })

  // The seven coarse field-service types the demo workspace ships with.
  // Used by the starter-set button when a workspace has zero service
  // lines. Operators can rename/delete after seeding.
  const STARTER_SET = [
    'HVAC', 'Plumbing', 'Electrical', 'General Repair',
    'Preventive Maintenance', 'Inspection', 'Emergency Service',
  ] as const

  const seedStarterMutation = useMutation({
    mutationFn: async () => {
      const rows = STARTER_SET.map((name, idx) => ({
        workspace_id: workspace!.id,
        name,
        sort_order: idx + 1,
      }))
      const { error } = await supabase.from('service_lines').insert(rows)
      if (error) throw error
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['service-lines'] })
      toast.success('Common field-service types added')
    },
    onError: (e: any) => toast.error(`Failed: ${e.message}`),
  })

  const updateMutation = useMutation({
    mutationFn: async ({ id, updates }: { id: string; updates: Record<string, any> }) => {
      const { error } = await supabase.from('service_lines').update(updates).eq('id', id)
      if (error) throw error
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['service-lines'] }),
    onError: (e: any) => toast.error(`Save failed: ${e.message}`),
  })

  const deleteMutation = useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase.from('service_lines').delete().eq('id', id)
      if (error) throw error
    },
    onSuccess: () => { queryClient.invalidateQueries({ queryKey: ['service-lines'] }); toast.success('Service line deleted') },
    onError: (e: any) => toast.error(`Failed: ${e.message}`),
  })

  return (
    <div>
      <Section title="Service Lines" description="The categories of work your business handles. Used by vendors and field jobs.">
        {isLoading ? (
          <p style={{ fontSize: 13, color: 'var(--ink-tertiary)', margin: 0 }}>Loading...</p>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            {serviceLines.length === 0 && !showAdd && (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 10, margin: '0 0 12px' }}>
                <p style={{ fontSize: 13, color: 'var(--ink-tertiary)', margin: 0 }}>
                  No service lines yet. Add HVAC, Plumbing, Electrical — whatever fits your operation.
                </p>
                <button onClick={() => seedStarterMutation.mutate()}
                  disabled={seedStarterMutation.isPending}
                  style={{ display: 'inline-flex', alignItems: 'center', gap: 7, padding: '8px 14px', alignSelf: 'flex-start', background: 'var(--accent-dim)', border: '1px solid var(--accent-border)', borderRadius: 8, color: 'var(--accent-text)', fontSize: 12, fontWeight: 700, cursor: 'pointer' }}>
                  <Sparkles style={{ width: 12, height: 12 }} />
                  {seedStarterMutation.isPending ? 'Adding…' : 'Add common field-service types'}
                </button>
              </div>
            )}
            {serviceLines.map((sl: any, i: number) => (
              <div key={sl.id}
                style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '12px 14px', background: 'var(--surface)', border: '1px solid var(--border-subtle)', borderRadius: 10 }}>
                {/* Reorder arrows — first/last disabled to short-circuit
                    a no-op mutation. Stacked vertically to keep the row
                    height stable across viewports. */}
                <div style={{ display: 'flex', flexDirection: 'column', gap: 2, flexShrink: 0 }}>
                  <button onClick={() => reorderMutation.mutate({ id: sl.id, direction: 'up' })}
                    disabled={i === 0 || reorderMutation.isPending}
                    aria-label="Move up"
                    style={{ background: 'none', border: 'none', cursor: i === 0 ? 'default' : 'pointer', color: i === 0 ? 'var(--border-default)' : 'var(--ink-tertiary)', padding: 2, display: 'flex', alignItems: 'center' }}>
                    <ArrowUp style={{ width: 12, height: 12 }} />
                  </button>
                  <button onClick={() => reorderMutation.mutate({ id: sl.id, direction: 'down' })}
                    disabled={i === serviceLines.length - 1 || reorderMutation.isPending}
                    aria-label="Move down"
                    style={{ background: 'none', border: 'none', cursor: i === serviceLines.length - 1 ? 'default' : 'pointer', color: i === serviceLines.length - 1 ? 'var(--border-default)' : 'var(--ink-tertiary)', padding: 2, display: 'flex', alignItems: 'center' }}>
                    <ArrowDown style={{ width: 12, height: 12 }} />
                  </button>
                </div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <input
                    defaultValue={sl.name}
                    onBlur={e => { if (e.target.value !== sl.name && e.target.value.trim()) updateMutation.mutate({ id: sl.id, updates: { name: e.target.value.trim() } }) }}
                    style={{ background: 'transparent', border: 'none', outline: 'none', color: 'var(--ink-primary)', fontSize: 13, fontWeight: 600, width: '100%', padding: 0, marginBottom: 2 }} />
                  <input
                    defaultValue={sl.description || ''}
                    onBlur={e => { if ((sl.description || '') !== e.target.value) updateMutation.mutate({ id: sl.id, updates: { description: e.target.value || null } }) }}
                    placeholder="Description (optional)"
                    style={{ background: 'transparent', border: 'none', outline: 'none', color: 'var(--ink-secondary)', fontSize: 12, width: '100%', padding: 0 }} />
                </div>
                <button onClick={() => updateMutation.mutate({ id: sl.id, updates: { is_active: !sl.is_active } })}
                  aria-label="Toggle active"
                  title={sl.is_active ? 'Active — visible in pickers' : 'Inactive — hidden in pickers, kept for historical reporting'}
                  style={{ width: 32, height: 18, borderRadius: 100, background: sl.is_active ? 'var(--positive)' : 'var(--border-default)', border: 'none', cursor: 'pointer', position: 'relative', flexShrink: 0 }}>
                  <div style={{ width: 14, height: 14, borderRadius: '50%', background: 'var(--accent-ink)', position: 'absolute', top: 2, left: sl.is_active ? 16 : 2, transition: 'left 150ms' }} />
                </button>
                <button onClick={() => { if (confirm(`Delete service line "${sl.name}"?`)) deleteMutation.mutate(sl.id) }}
                  aria-label="Delete"
                  style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--ink-tertiary)', padding: 6, display: 'flex', alignItems: 'center', borderRadius: 6, flexShrink: 0 }}
                  onMouseEnter={e => e.currentTarget.style.color = 'var(--danger)'}
                  onMouseLeave={e => e.currentTarget.style.color = 'var(--ink-tertiary)'}>
                  <Trash2 style={{ width: 14, height: 14 }} />
                </button>
              </div>
            ))}

            {showAdd ? (
              <div style={{ padding: '14px', background: 'var(--accent-dim)', border: '1px solid var(--accent-dim)', borderRadius: 10, display: 'flex', flexDirection: 'column', gap: 10 }}>
                <input value={form.name} onChange={e => setForm(p => ({ ...p, name: e.target.value }))} placeholder="Name (e.g. HVAC)" autoFocus style={inp}
                  onFocus={e => e.currentTarget.style.borderColor = 'var(--focus-ring)'}
                  onBlur={e => e.currentTarget.style.borderColor = 'var(--border-default)'} />
                <input value={form.description} onChange={e => setForm(p => ({ ...p, description: e.target.value }))} placeholder="Description (optional)" style={inp}
                  onFocus={e => e.currentTarget.style.borderColor = 'var(--focus-ring)'}
                  onBlur={e => e.currentTarget.style.borderColor = 'var(--border-default)'} />
                <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
                  <button onClick={() => { setShowAdd(false); setForm({ name: '', description: '' }) }} className="btn-ghost" style={{ fontSize: 12 }}>Cancel</button>
                  <button onClick={() => { if (form.name.trim()) createMutation.mutate(form) }}
                    disabled={!form.name.trim() || createMutation.isPending} className="btn-primary" style={{ fontSize: 12 }}>
                    {createMutation.isPending ? 'Adding...' : 'Add'}
                  </button>
                </div>
              </div>
            ) : (
              <button onClick={() => setShowAdd(true)}
                style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6, padding: '10px', background: 'var(--surface)', border: '1px dashed var(--border-default)', borderRadius: 10, color: 'var(--ink-secondary)', fontSize: 13, fontWeight: 600, cursor: 'pointer', transition: 'all 150ms' }}
                onMouseEnter={e => { e.currentTarget.style.color = 'var(--accent-text)'; e.currentTarget.style.borderColor = 'var(--accent-border)' }}
                onMouseLeave={e => { e.currentTarget.style.color = 'var(--ink-secondary)'; e.currentTarget.style.borderColor = 'var(--border-default)' }}>
                <Plus style={{ width: 14, height: 14 }} /> Add service line
              </button>
            )}
          </div>
        )}
      </Section>
    </div>
  )
}

// ─── Canned Responses ─────────────────────────────────────────────────────────
type CannedResponse = {
  id: string
  workspace_id: string
  name: string
  shortcut: string | null
  content: string
  category: string | null
}

function CannedResponsesSettings() {
  const { workspace } = useAuth()
  const queryClient = useQueryClient()
  const [editing, setEditing] = useState<CannedResponse | 'new' | null>(null)

  const { data: items = [], isLoading } = useQuery<CannedResponse[]>({
    queryKey: ['canned-responses', workspace?.id],
    queryFn: async () => {
      if (!workspace?.id) return []
      const { data, error } = await supabase
        .from('canned_responses')
        .select('*')
        .eq('workspace_id', workspace.id)
        .order('name')
      if (error) throw error
      return (data ?? []) as CannedResponse[]
    },
    enabled: !!workspace?.id,
  })

  const deleteMutation = useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase.from('canned_responses').delete().eq('id', id)
      if (error) throw error
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['canned-responses', workspace?.id] })
      toast.success('Canned response deleted')
    },
    onError: () => toast.error('Failed to delete'),
  })

  return (
    <div>
      <Section
        title="Canned responses"
        description="Reusable reply snippets your team can drop into conversations. Type / in the reply box to pick one."
      >
        <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 14 }}>
          <button onClick={() => setEditing('new')} className="btn-primary">
            <Plus style={{ width: 14, height: 14 }} /> New canned response
          </button>
        </div>

        {isLoading ? (
          <p style={{ fontSize: 13, color: 'var(--ink-tertiary)', margin: 0 }}>Loading…</p>
        ) : items.length === 0 ? (
          <div style={{ padding: '32px 20px', textAlign: 'center', background: 'var(--surface)', border: '1px dashed var(--border-subtle)', borderRadius: 12 }}>
            <Zap style={{ width: 28, height: 28, color: 'var(--border-default)', margin: '0 auto 8px', display: 'block' }} />
            <p style={{ fontSize: 13, color: 'var(--ink-secondary)', margin: '0 0 4px', fontWeight: 600 }}>No canned responses yet</p>
            <p style={{ fontSize: 12, color: 'var(--ink-tertiary)', margin: 0 }}>Create one to speed up common replies.</p>
          </div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            {items.map(item => (
              <div key={item.id} style={{ padding: '12px 14px', background: 'var(--surface)', border: '1px solid var(--border-subtle)', borderRadius: 10, display: 'flex', alignItems: 'flex-start', gap: 12 }}>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4, flexWrap: 'wrap' }}>
                    <span style={{ fontSize: 13, fontWeight: 700, color: 'var(--ink-primary)' }}>{item.name}</span>
                    {item.shortcut && (
                      <span style={{ fontSize: 10, fontWeight: 700, color: 'var(--accent)', background: 'var(--accent-dim)', padding: '1px 6px', borderRadius: 5, fontFamily: 'monospace' }}>{item.shortcut}</span>
                    )}
                    {item.category && (
                      <span style={{ fontSize: 10, fontWeight: 600, color: 'var(--ink-secondary)', background: 'var(--border-subtle)', padding: '1px 6px', borderRadius: 5 }}>{item.category}</span>
                    )}
                  </div>
                  <p style={{ fontSize: 12, color: 'var(--ink-secondary)', margin: 0, lineHeight: 1.45, overflow: 'hidden', textOverflow: 'ellipsis', display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical' }}>
                    {item.content}
                  </p>
                </div>
                <div style={{ display: 'flex', gap: 6, flexShrink: 0 }}>
                  <button onClick={() => setEditing(item)} title="Edit"
                    style={{ width: 30, height: 30, borderRadius: 8, background: 'var(--hover-bg)', border: '1px solid var(--border-subtle)', cursor: 'pointer', color: 'var(--ink-secondary)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <Save style={{ width: 13, height: 13 }} />
                  </button>
                  <button onClick={() => { if (confirm(`Delete "${item.name}"?`)) deleteMutation.mutate(item.id) }} title="Delete"
                    style={{ width: 30, height: 30, borderRadius: 8, background: 'var(--danger-dim)', border: '1px solid var(--danger-border)', cursor: 'pointer', color: 'var(--danger)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <Trash2 style={{ width: 13, height: 13 }} />
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </Section>

      {editing && (
        <CannedResponseEditor
          item={editing === 'new' ? null : editing}
          onClose={() => setEditing(null)}
        />
      )}
    </div>
  )
}

function CannedResponseEditor({ item, onClose }: { item: CannedResponse | null; onClose: () => void }) {
  const { workspace } = useAuth()
  const queryClient = useQueryClient()
  const [name, setName] = useState(item?.name ?? '')
  const [shortcut, setShortcut] = useState(item?.shortcut ?? '')
  const [content, setContent] = useState(item?.content ?? '')
  const [category, setCategory] = useState(item?.category ?? '')
  const [saving, setSaving] = useState(false)

  const handleSave = async () => {
    if (!workspace?.id) return
    if (!name.trim()) { toast.error('Name is required'); return }
    if (!content.trim()) { toast.error('Content is required'); return }
    setSaving(true)
    const payload = {
      workspace_id: workspace.id,
      name: name.trim(),
      shortcut: shortcut.trim() || null,
      content: content.trim(),
      category: category.trim() || null,
    }
    const { error } = item
      ? await supabase.from('canned_responses').update(payload).eq('id', item.id)
      : await supabase.from('canned_responses').insert(payload)
    setSaving(false)
    if (error) { toast.error(`Failed to save: ${error.message}`); return }
    queryClient.invalidateQueries({ queryKey: ['canned-responses', workspace.id] })
    toast.success(item ? 'Canned response updated' : 'Canned response created')
    onClose()
  }

  return (
    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
      style={{ position: 'fixed', inset: 0, background: 'var(--overlay-scrim)', backdropFilter: 'blur(4px)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 100, padding: 24 }}
      onClick={onClose}>
      <motion.div initial={{ opacity: 0, scale: 0.95, y: 12 }} animate={{ opacity: 1, scale: 1, y: 0 }} exit={{ opacity: 0, scale: 0.95 }} transition={{ duration: 0.15 }}
        onClick={e => e.stopPropagation()}
        style={{ background: 'var(--surface)', border: '1px solid var(--border-default)', borderRadius: 20, padding: 28, width: '100%', maxWidth: 540, maxHeight: '90vh', display: 'flex', flexDirection: 'column' }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 24, flexShrink: 0 }}>
          <h2 style={{ fontSize: 18, fontWeight: 800, color: 'var(--ink-primary)', margin: 0 }}>{item ? 'Edit canned response' : 'New canned response'}</h2>
          <button onClick={onClose} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--ink-tertiary)', padding: 0 }}><X style={{ width: 18, height: 18 }} /></button>
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 14, overflowY: 'auto', minHeight: 0 }}>
          <div>
            <label style={{ fontSize: 11, fontWeight: 600, color: 'var(--ink-tertiary)', display: 'block', marginBottom: 6, textTransform: 'uppercase', letterSpacing: '0.06em' }}>Name *</label>
            <input value={name} onChange={e => setName(e.target.value)} placeholder="Greeting" style={inp}
              onFocus={e => e.currentTarget.style.borderColor = 'var(--focus-ring)'}
              onBlur={e => e.currentTarget.style.borderColor = 'var(--border-default)'} />
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
            <div>
              <label style={{ fontSize: 11, fontWeight: 600, color: 'var(--ink-tertiary)', display: 'block', marginBottom: 6, textTransform: 'uppercase', letterSpacing: '0.06em' }}>Shortcut</label>
              <input value={shortcut} onChange={e => setShortcut(e.target.value)} placeholder="/hi" style={{ ...inp, fontFamily: 'monospace' }}
                onFocus={e => e.currentTarget.style.borderColor = 'var(--focus-ring)'}
                onBlur={e => e.currentTarget.style.borderColor = 'var(--border-default)'} />
            </div>
            <div>
              <label style={{ fontSize: 11, fontWeight: 600, color: 'var(--ink-tertiary)', display: 'block', marginBottom: 6, textTransform: 'uppercase', letterSpacing: '0.06em' }}>Category</label>
              <input value={category} onChange={e => setCategory(e.target.value)} placeholder="Greetings" style={inp}
                onFocus={e => e.currentTarget.style.borderColor = 'var(--focus-ring)'}
                onBlur={e => e.currentTarget.style.borderColor = 'var(--border-default)'} />
            </div>
          </div>
          <div>
            <label style={{ fontSize: 11, fontWeight: 600, color: 'var(--ink-tertiary)', display: 'block', marginBottom: 6, textTransform: 'uppercase', letterSpacing: '0.06em' }}>Content *</label>
            <textarea value={content} onChange={e => setContent(e.target.value)} rows={5}
              placeholder="Hi! Thanks for reaching out. How can I help you today?"
              style={{ ...inp, resize: 'vertical', lineHeight: 1.5, fontFamily: 'inherit' }}
              onFocus={e => e.currentTarget.style.borderColor = 'var(--focus-ring)'}
              onBlur={e => e.currentTarget.style.borderColor = 'var(--border-default)'} />
          </div>
        </div>
        <div style={{ display: 'flex', gap: 10, marginTop: 24, flexShrink: 0 }}>
          <button onClick={onClose} className="btn-ghost" style={{ flex: 1, justifyContent: 'center' }}>Cancel</button>
          <button onClick={handleSave} disabled={saving} className="btn-primary" style={{ flex: 1, justifyContent: 'center' }}>
            {saving ? 'Saving…' : item ? 'Save changes' : 'Create'}
          </button>
        </div>
      </motion.div>
    </motion.div>
  )
}

// ─── Main Settings Layout ─────────────────────────────────────────────────────
export default function Settings() {
  const location = useLocation()
  const navigate = useNavigate()
  const isMobile = useIsMobile()
  const { data: access } = useWorkspaceAccess()
  const { profile } = useAuth()
  const isOwnerAdmin = !!profile?.is_owner || profile?.role === 'admin'

  const activeSection = location.pathname.split('/settings/')[1] || ''

  // Tier-supports-AI gate on the AI nav entry AND the section render. Gating
  // on tier rather than current opt-in state ensures toggling AI off doesn't
  // hide the way back on. Tiers that grant 'ai' in get_workspace_limits are
  // pro and enterprise. A starter/growth/free user navigating directly to
  // /settings/ai will fall through to the WorkspaceSettings default since
  // SECTIONS['ai'] is absent from the map.
  const tierSupportsAI = access?.tier === 'pro' || access?.tier === 'enterprise'
  const navItems = SETTINGS_NAV.filter(item => {
    if (item.path === 'billing') return SHOW_BILLING_TAB
    if (item.path === 'ai') return tierSupportsAI
    if (item.path === 'appearance') return SHOW_APPEARANCE_TAB
    if (item.path === 'pipeline') return isOwnerAdmin   // owner/admin-only (UI gate)
    if (item.path === 'audit') return isOwnerAdmin      // owner/admin-only (sensitive)
    return true
  })

  const SECTIONS: Record<string, React.ReactNode> = {
    '': <WorkspaceSettings />,
    'team': <TeamSettings />,
    ...(SHOW_BILLING_TAB ? { 'billing': <BillingSettings /> } : {}),
    'invoicing': <InvoicingSettings />,
    ...(tierSupportsAI ? { 'ai': <AISettings /> } : {}),
    'service-lines': <ServiceLinesSettings />,
    'pipeline': <PipelineSettings />,
    'audit': <AuditLogSettings />,
    'terminology': <TerminologySettings />,
    'canned': <CannedResponsesSettings />,
    'notifications': <NotificationsSettings />,
    ...(SHOW_APPEARANCE_TAB ? { 'appearance': <AppearanceSettings /> } : {}),
    'security': <SecuritySettings />,
  }

  return (
    <div style={{ height: '100%', display: 'flex', flexDirection: isMobile ? 'column' : 'row', background: 'var(--canvas)' }}>
      {/* Sidebar — desktop renders an inline 220px nav rail. On mobile
          (<768px) the rail would consume most of the viewport, so we
          render the same nav as a horizontally-scrollable strip of
          pill buttons above the content instead. Active section is
          driven by the same route → activeSection state, so the only
          difference is layout. */}
      {!isMobile && (
      <div style={{ width: 220, borderRight: '1px solid var(--border-subtle)', padding: '24px 12px', flexShrink: 0 }}>
        <p style={{ fontSize: 11, fontWeight: 700, color: 'var(--ink-tertiary)', textTransform: 'uppercase', letterSpacing: '0.08em', margin: '0 0 10px 10px' }}>Settings</p>
        <nav style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          {navItems.map(item => {
            const isActive = activeSection === item.path
            return (
              <button key={item.path} onClick={() => navigate(`/app/settings${item.path ? `/${item.path}` : ''}`)}
                style={{ display: 'flex', alignItems: 'center', gap: 9, padding: '8px 12px', borderRadius: 9, fontSize: 13, fontWeight: isActive ? 600 : 400, color: isActive ? 'var(--accent-text)' : 'var(--ink-secondary)', background: isActive ? 'var(--accent-dim)' : 'transparent', border: 'none', cursor: 'pointer', textAlign: 'left', width: '100%', transition: 'all 150ms ease' }}
                onMouseEnter={e => { if (!isActive) e.currentTarget.style.background = 'var(--hover-bg)' }}
                onMouseLeave={e => { if (!isActive) e.currentTarget.style.background = 'transparent' }}>
                <item.icon style={{ width: 15, height: 15, flexShrink: 0 }} />
                {item.label}
              </button>
            )
          })}
        </nav>
      </div>
      )}

      {isMobile && (
        <nav
          aria-label="Settings sections"
          style={{
            display: 'flex', gap: 6,
            padding: '12px 14px',
            overflowX: 'auto',
            borderBottom: '1px solid var(--border-subtle)',
            background: 'var(--surface)',
            flexShrink: 0,
            scrollbarWidth: 'none',
          }}
        >
          {navItems.map(item => {
            const isActive = activeSection === item.path
            return (
              <button key={item.path} onClick={() => navigate(`/app/settings${item.path ? `/${item.path}` : ''}`)}
                style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '7px 12px', borderRadius: 9, fontSize: 12, fontWeight: isActive ? 600 : 500, color: isActive ? 'var(--accent-text)' : 'var(--ink-secondary)', background: isActive ? 'var(--accent-dim)' : 'var(--hover-bg)', border: isActive ? '1px solid var(--accent-border)' : '1px solid var(--border-subtle)', cursor: 'pointer', whiteSpace: 'nowrap', flexShrink: 0, transition: 'all 150ms ease' }}>
                <item.icon style={{ width: 13, height: 13, flexShrink: 0 }} />
                {item.label}
              </button>
            )
          })}
        </nav>
      )}

      {/* Content */}
      <div style={{ flex: 1, overflow: 'auto', padding: isMobile ? '20px 16px' : '28px 32px' }}>
        <motion.div key={activeSection} initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.15 }}>
          <h1 style={{ fontSize: 22, fontWeight: 900, color: 'var(--ink-primary)', letterSpacing: '-0.03em', margin: '0 0 24px' }}>
            {navItems.find(n => n.path === activeSection)?.label || 'Workspace'}
          </h1>
          {SECTIONS[activeSection] || <WorkspaceSettings />}
        </motion.div>
      </div>
    </div>
  )
}